// CONTATOS — a mesma tela em DUAS janelas ([DA-691] o dia, [DA-705] a base inteira).
//
// ⛔ **REGRA FECHADA, decisao dele em 10/09/2026 — nao e' escolha de implementacao:** *"As duas
// abas terao o mesmo componente. Toda coluna que tiver em uma, tera na outra."* Entao **coluna
// nova entra pras DUAS**, sempre; se ela so' faz sentido numa das janelas, o que muda e' o que a
// celula IMPRIME, nunca a existencia da coluna. Uma coluna que exista so' num modo e' violacao da
// regra, nao otimizacao — e a primeira vez que isso acontecer as duas telas comecam a divergir.
//
// ⚠️ Por isso a peca e' uma so', com a prop `modo`. Duas copias do render era o caminho curto e o
// errado mesmo antes da regra: sao ~250 linhas de linha-de-contato com oito larguras FIXAS medidas
// na tela publicada ([DA-700] p.4, [DA-703], [DA-705]) — dois arquivos divergiriam na primeira
// medicao que so' um deles recebesse.
//
// ⛔ **Cada coluna responde a MESMA pergunta nas duas janelas, so' muda o alcance**: o `carimbo` e'
// a hora no dia e a DATA da ultima mensagem na base; `mensagens` e' o do dia e o TOTAL; `vendas`
// sao as do dia e a ULTIMA compra; `flys` sao os da janela e o ULTIMO. Por isso os campos do
// contrato tem nome NEUTRO — `hora` e `vendas_dia` mentiriam metade do tempo ([DA-648]: nome igual,
// objeto diferente, e' o erro que mais dura escondido).
//
// Pedido dele em 08/09/2026: *"uma aba com a relacao dos contatos que nos enviou mensagens no dia
// [...] a ideia e' marcar todos os contatos com uma ou mais etiqueta, e a nova aba vai nos ajudar a
// verificar que os novos contatos tambem sejam marcados"*. Escolhas dele na mesma conversa:
// **marcar ali mesmo** (nao so' exibir) e **so' a caixa Atendimento**.
//
// ⚠️ **A aba nao leva contador no rotulo**, pelo mesmo motivo que a Agenda nao leva: as outras
// contam ORCAMENTO na janela e esta conta CONTATO no dia — numero ali seria comparado com os
// vizinhos e nao fala a mesma lingua. O placar do que falta vive na faixa acima da lista, onde
// tem dono escrito.
import { useCallback, useEffect, useState } from 'react';
import { Tag, RefreshCw, MessageCircle, Pencil, Plus, Minus, Search, Send, Loader2, Megaphone, Trash2, BarChart3, List } from 'lucide-react';
import { contar, plural, formatTelefoneBR, formatDataSP, formatHoraSP } from '@fiodecobre/shared';
import { api } from '../../lib/api';
import { Avatar } from '../../components/Avatar';
import { FotoProduto } from '../../components/FotoProduto';
import { useToast } from '../../components/Toast';
import { BadgeEtiqueta } from '../atendimento/componentes/BadgeEtiqueta';
import { LightboxImagem } from '../atendimento/componentes/LightboxImagem';
import { SeletorEtiquetas } from '../atendimento/componentes/SeletorEtiquetas';
import { useEtiquetas, type Etiqueta } from '../atendimento/lib/etiquetas-context';
import { atendimentoApi } from '../atendimento/lib/api';
import { ModalEtiquetas } from '../atendimento/modais/ModalEtiquetas';
import { ModalEnviarMaterial } from '../atendimento/componentes/ModalEnviarMaterial';
import { ModalCampanhaFly } from './ModalCampanhaFly';
import { useDialog } from '../../components/DialogProvider';
import { excluirContatoDeVez } from './excluir-contato';
import { ComboBox } from '../../components/ComboBox';
import { Modal } from '../../components/Modal';

/**
 * Uma venda FINALIZADA ligada ao contato pelo cadastro ou pelo telefone ([DA-686]).
 *
 * No modo `dia` sao as vendas daquele dia; no modo `base`, a ULTIMA compra do contato — uma so'.
 */
interface VendaDia {
  numero: string;
  total: string;
  vendedor: string | null;
  /** Abre os itens ([DA-703]). `null` no finalizado que ainda nao virou venda — ai nao ha `venda_itens`. */
  venda_id: number | null;
  /** So' no modo `base`: quando a compra fechou. No dia a data e' o proprio recorte da aba. */
  quando?: string | null;
}

/**
 * Um item comprado, como a rota do Caixa ja devolve ([DA-703]).
 *
 * ⛔ Vem de `venda_itens`, NUNCA de `orcamento_itens`: depois de cancelar + reeditar +
 * refinalizar, os dois divergem, e o canonico e' o da venda — e' o que o `GET /api/caixa/
 * vendas/:id/itens` ja diz no proprio comentario. Reusar a rota inteira em vez de escrever
 * outra evita herdar essa armadilha pela metade.
 */
interface ItemComprado {
  id: number;
  quantidade: string;
  preco_unitario: string;
  subtotal: string;
  produto_descricao: string | null;
  codigo_interno: string | null;
  foto_url: string | null;
  unidade_sigla: string | null;
}

/** Um fly que a loja mandou pro contato na janela da fila ([DA-682] / [DA-683]). */
interface FlyDia {
  quando: string;
  quem: string | null;
  de_promocao: boolean;
  /** `null` quando o fly nao carrega produto — ver o porque na celula. */
  produtos: string | null;
}

interface ContatoDia {
  id: number;
  nome: string | null;
  /** O MESMO campo que o chat ja imprime junto do nome — a aba e' que nao o trazia ([DA-701]). */
  sobrenome: string | null;
  push_name: string | null;
  telefone: string | null;
  foto_url: string | null;
  /**
   * O WhatsApp NAO entrega neste contato (jid corrompido, lista, grupo) — a mesma regra do
   * "numero nao entrega" da campanha (`sqlNumeroEntrega`). E' a unica linha que ganha a lixeira.
   */
  numero_invalido: boolean;
  conversa_id: number | null;
  /**
   * O que a celula da direita imprime: **hora** no modo `dia` (`14:32`), **data** no modo `base`
   * (`08/09/26`). Nome neutro de proposito — chamado de `hora`, ele mentiria na base inteira.
   * `null` no contato que nunca escreveu (na base, `?todos=1`) e tambem no que a loja so'
   * PROCUROU hoje: ele nao mandou nada, entao esta celula nao tem o que dizer — o horario dele
   * mora em `ultima_msg`.
   */
  carimbo: string | null;
  mensagens: number;
  /**
   * Por onde a pessoa apareceu ([DA-703], decisao dele) — no dia, por onde apareceu HOJE.
   * ⚠️ `null` no modo `base` pra quem nunca escreveu NEM comprou (os importados de 28/03): ali
   * `BALCAO` seria invencao, porque a pessoa nunca pisou na loja pelo que o sistema sabe.
   */
  origem: 'CHAT' | 'BALCAO' | null;
  /** TODAS as etiquetas do contato, ocultas inclusive — ver o porque no `gravar` abaixo. */
  etiquetas: Etiqueta[];
  sem_etiqueta: boolean;
  /** `null` quando nao houve compra — a coluna fica vazia, e' o "quando houver". */
  vendas: VendaDia[] | null;
  /** `null` quando nao houve fly — mesmo "quando houver" da coluna de vendas. */
  flys: FlyDia[] | null;
  /** Quem cuidou do contato no chat (o "Direcionado:" do Atendimento). */
  atendente: string | null;
  /**
   * Carimbo da última mensagem outbound enviada ao contato — `HH:MM` quando foi hoje,
   * `DD/MM` quando foi outro dia. `null` quando nunca enviamos nada a essa pessoa.
   */
  ultima_msg: string | null;
  /** Nome do cliente vinculado (`razao_social ?? nome`). `null` para CONSUMIDOR ou sem vínculo. */
  cliente_nome: string | null;
  /**
   * Os sinais do MAPA do dia (botao "Estatistica", 25/09/2026) — so' a rota `contatos-dia` os
   * manda; na base vem `undefined`. O que cada um significa esta no comentario da rota.
   */
  falamos_escrito?: boolean;
  /** Escreveu hoje algo que nao e' resposta automatica do WhatsApp Business dele. */
  escreveu_humano?: boolean;
  fly_hoje?: boolean;
  sem_resposta?: boolean;
  contato_novo?: boolean;
  escreveu_apos_fly?: boolean;
}

/**
 * O MAPA DO DIA — botao "Estatistica" da aba (pedido dele em 25/09/2026: *"esta tela sao os
 * contatos do dia, e' o momento mais quente para tomarmos alguma acao"*). Os tres primeiros
 * sinais foram os que ele nomeou; os quatro de baixo, escolha dele entre os que eu medi.
 *
 * ⛔ **Conta sobre a PROPRIA lista, nunca numa consulta a parte** — e' o que garante que o numero
 * do quadro e o que o clique filtra sao o mesmo conjunto ([DA-410]: duas telas respondendo a mesma
 * pergunta com numeros diferentes). Um contato cai em mais de um quadro, e o mapa DIZ isso.
 */
export type SinalMapa = 'escreveu' | 'falamos' | 'fly' | 'sem_resposta' | 'novo' | 'apos_fly' | 'comprou';

/** ⛔ Os MESMOS quadros alimentam o mapa do MES (aba Estatisticas) — rotulo e texto saem daqui, e o
 *  `teste` tem espelho no `contarMapaDoDia` do servidor. Mexeu num, mexa no outro. */
export const SINAIS_MAPA: Array<{
  chave: SinalMapa;
  grupo: 'movimento' | 'acao';
  rotulo: string;
  explica: string;
  teste: (c: ContatoDia) => boolean;
}> = [
  { chave: 'escreveu', grupo: 'movimento', rotulo: 'Escreveram pra loja',
    explica: 'Mandaram ao menos uma mensagem hoje (resposta automatica nao conta).', teste: (c) => !!c.escreveu_humano },
  { chave: 'falamos', grupo: 'movimento', rotulo: 'A loja procurou',
    explica: 'A primeira mensagem do dia foi nossa, antes do cliente escrever (fly e nota da compra nao contam).', teste: (c) => !!c.falamos_escrito },
  { chave: 'fly', grupo: 'movimento', rotulo: 'Receberam fly',
    explica: 'O sistema ou a vendedora mandou fly hoje.', teste: (c) => !!c.fly_hoje },
  { chave: 'sem_resposta', grupo: 'acao', rotulo: 'Sem resposta',
    explica: 'A ultima mensagem do dia foi deles. Nao conta quem so encerrou ("ok", "obrigado", joinha) nem quem a loja respondeu com uma reacao.',
    teste: (c) => !!c.sem_resposta },
  { chave: 'novo', grupo: 'acao', rotulo: 'Contato novo',
    explica: 'Escreveram hoje pela primeira vez na historia.', teste: (c) => !!c.contato_novo },
  { chave: 'apos_fly', grupo: 'acao', rotulo: 'Responderam o fly',
    explica: 'Receberam fly hoje e escreveram em seguida, sem outra conversa no meio.', teste: (c) => !!c.escreveu_apos_fly },
  { chave: 'comprou', grupo: 'acao', rotulo: 'Compraram hoje',
    explica: 'Tem venda fechada hoje ligada ao contato.', teste: (c) => (c.vendas?.length ?? 0) > 0 },
];

/** A campanha de fly aberta nesta aba do navegador — lida no F5 pra reabrir a tela. */
const CHAVE_CAMPANHA_ABERTA = 'campanha-fly:aberta';
/** O quadro do mapa do dia que filtra a lista — lido na volta do chat. */
const CHAVE_FILTRO_MAPA = 'contatos-dia:filtro-mapa';

const fmtBRL = (v: number | string) =>
  Number(v || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

// O contato costuma ter nome vazio, so' pontuacao ou o proprio numero. A ordem e' a mesma que o
// Atendimento usa: o que a loja escreveu, senao o nome do WhatsApp, senao o telefone.
//
// ⚠️ **O sobrenome entra colado no nome, como no chat** (`[nome, sobrenome].join(' ')`, o mesmo
// que `ListaConversas`, `PainelChat`, `PainelContato` e `ModalContatos` fazem) — pedido dele em
// 09/09/2026. ⛔ Ele NAO entra no ramo do `push_name`: aquele texto e' o que a PESSOA escolheu no
// aparelho dela, e emendar nele um sobrenome que a loja digitou faria um nome que nunca existiu.
function nomeDe(c: ContatoDia): string {
  const nome = [c.nome, c.sobrenome].map((s) => (s ?? '').trim()).filter(Boolean).join(' ');
  if (nome && nome !== '.') return nome;
  const push = (c.push_name ?? '').trim();
  if (push) return push;
  return c.telefone ? formatTelefoneBR(c.telefone) : `Contato ${c.id}`;
}

/**
 * O nome SEM o sobrenome, pra coluna propria ([DA-705], pedido dele em 10/09/2026: *"vamos separar
 * esta coluna em duas, Nome e Sobrenome. Dessa forma fica mais facil visualizar quem tem o
 * sobrenome junto com o nome"*).
 *
 * ⚠️ **A coluna existe pra mostrar o VAZIO**: so' 22 dos 1.137 contatos tem sobrenome preenchido
 * (medido em 10/09/2026), entao ela fica em branco em 98% das linhas — e e' exatamente isso que
 * ele quer enxergar. Vazio aqui e' o dado, nao falta de dado.
 *
 * ⛔ Os fallbacks (push_name, telefone) continuam sendo do NOME, nunca do sobrenome: o `nomeDe`
 * acima ja explica por que o sobrenome nao se emenda no `push_name` — aqui a razao e' outra e leva
 * ao mesmo lugar. Se a coluna Sobrenome herdasse fallback, ela deixaria de responder "este cadastro
 * tem sobrenome?", que e' a unica pergunta que ela faz.
 *
 * ⛔ **Esta regra tem uma COPIA em SQL**: o `nome_ordem` das duas rotas de contato ordena a lista
 * por ela, com o MESMO descarte do `.` — sem isso a lista abre com os 7 contatos cujo nome gravado
 * e' um ponto, ordenados por um texto que a tela nao imprime ([DA-705]). Mexeu aqui, mexa la.
 */
function soNome(c: ContatoDia): string {
  const nome = (c.nome ?? '').trim();
  if (nome && nome !== '.') return nome;
  const push = (c.push_name ?? '').trim();
  if (push) return push;
  return c.telefone ? formatTelefoneBR(c.telefone) : `Contato ${c.id}`;
}

/** So' o campo do cadastro, sem fallback nenhum — ver o porque no `soNome`. */
function soSobrenome(c: ContatoDia): string {
  return (c.sobrenome ?? '').trim();
}

/**
 * As larguras das colunas, em PIXEL, lidas pelo CABECALHO e pela LINHA ([DA-707], 10/09/2026).
 *
 * ⛔ **Existe pra que os dois nao divirjam.** Antes do cabecalho as larguras viviam soltas em cada
 * celula; a partir do momento em que ha um titulo por cima delas, um numero mudado num lugar so'
 * desalinha o rotulo do conteudo — e o desalinhamento nao quebra teste nenhum, so' aparece na tela.
 *
 * ⚠️ Cada numero aqui foi MEDIDO na tela publicada, nao escolhido: 110 pro sobrenome ([DA-705]),
 * 92 pra quem atendeu ([DA-700]), 176 pra compra ([DA-703]), 180 pras etiquetas ([DA-703]).
 * Mexer num deles pede a medicao de novo, com a sonda de largura do [DA-705].
 *
 * ⚠️ Em PIXEL e nao no `w-*` do Tailwind: a raiz do app e' 14px e o `w-*` e' rem — `w-64` daria
 * 224 e nao 256.
 */
const COL = {
  /** O `+` que abre os itens da compra ([DA-703]) — reservado ate na linha sem venda. */
  abrir: 22,
  /** A foto do contato. */
  avatar: 38,
  sobrenome: 110,
  /** O lapis de editar nome/sobrenome ([DA-705]). */
  lapis: 32,
  atendeu: 92,
  ultEnvio: 58,
  compra: 176,
  flys: 236,
  etiquetas: 180,
  /** O balaozinho que abre a conversa no Atendimento + o aviaozinho do fly ([DA-707]). */
  chat: 64,
  origem: 62,
  recebidas: 58,
} as const;

/**
 * O vao do `gap-3` entre as colunas, em PIXEL.
 *
 * ⛔ **10,5 e nao 12**: `gap-3` e' `0.75rem` e a raiz do app e' **14px**, nao 16 — a mesma
 * armadilha que ja fez `w-64` valer 224 nesta tela ([DA-703]) e a tabela da fila ser 154 onde o
 * comentario dizia 176 ([DA-671]). Conferido na tela publicada antes de publicar: com 12 os riscos
 * caiam 3,5px pra dentro da coluna da direita, e o erro CRESCE a cada coluna somada.
 */
const VAO = 10.5;

/**
 * Os RISCOS VERTICAIS entre as colunas ([DA-707], pedido dele em 10/09/2026 com as sete linhas
 * desenhadas no print).
 *
 * ⛔ **Medidos a partir da DIREITA, e por isso a conta fecha**: da coluna `Sobrenome` pra direita
 * e' tudo largura fixa; o unico elastico e' o Nome, que fica a ESQUERDA de todos eles. Medir da
 * esquerda exigiria saber quanto o Nome ocupa — que so' se sabe depois do layout, e muda com o
 * tamanho da janela.
 *
 * ⚠️ **Sai de `COL`, nao de numero escrito a mao**: mexer numa largura reposiciona os riscos
 * sozinho. Numero copiado e' o jeito conhecido de o risco descolar da coluna que ele separa.
 *
 * ⛔ **`risco` e' a linha ANTES da coluna, e o `false` gruda um botao no vizinho certo** — o lapis
 * fica do lado do Sobrenome que ele edita, e o balaozinho entra na mesma caixa da Origem. E' o
 * que o print dele mostra: a primeira linha vem DEPOIS do lapis, e a de baixo ANTES do balaozinho.
 *
 * ⚠️ **A ORDEM daqui e' a ordem da TELA** — cabecalho e linha. Trocar coluna de lugar e nao mexer
 * aqui poe os riscos nos vaos errados, calado: a conta continua fechando, so' que sobre outra
 * sequencia de larguras.
 */
const COLUNAS_A_DIREITA: { largura: number; risco: boolean }[] = [
  { largura: COL.sobrenome, risco: true },
  { largura: COL.lapis, risco: false },
  { largura: COL.atendeu, risco: true },
  { largura: COL.compra, risco: true },
  { largura: COL.flys, risco: true },
  { largura: COL.etiquetas, risco: true },
  { largura: COL.chat, risco: true },
  { largura: COL.origem, risco: false },
  // ⚠️ `Últ. envio` mudou de lugar em 10/09/2026, a pedido dele: *"vamos colocar a coluna Ult.
  //    envio ao lado esquerdo de Recebidas, vai fazer mais sentido"*. Faz: as duas falam de
  //    mensagem e so' se distinguem pela DIRECAO — o que a loja mandou e o que o contato escreveu.
  //    Separadas por meia tela, a comparacao exigia varrer a linha inteira.
  { largura: COL.ultEnvio, risco: true },
  { largura: COL.recebidas, risco: true },
];

/**
 * A distancia de cada risco ate a borda direita do conteudo, em pixel.
 *
 * Percorre as colunas de tras pra frente somando largura + vao; o risco fica no MEIO do vao
 * (`+ VAO / 2`), nunca colado na coluna — colado, ele le como borda de uma delas.
 */
const RISCOS: number[] = (() => {
  const saida: number[] = [];
  let daDireita = 0;
  for (let i = COLUNAS_A_DIREITA.length - 1; i >= 0; i--) {
    const c = COLUNAS_A_DIREITA[i]!;
    daDireita += c.largura;
    if (c.risco) saida.push(daDireita + VAO / 2);
    daDireita += VAO;
  }
  return saida;
})();

/**
 * Os riscos de uma linha (ou do cabecalho).
 *
 * ⚠️ `top`/`bottom` NEGATIVOS de proposito: o risco tem que atravessar o `py` do card, senao ele
 * nasce e morre no meio da altura e a lista fica com tracinhos soltos. ⛔ E ele mora DENTRO da
 * fileira de colunas, nao no card: assim ele para antes da gaveta dos itens e do seletor de
 * etiquetas, que sao blocos de largura inteira e nao tem coluna nenhuma pra separar.
 */
function RiscosDeColuna({ cor, alturaExtra }: { cor: string; alturaExtra: number }) {
  return (
    <>
      {RISCOS.map((r) => (
        <span
          key={r}
          aria-hidden="true"
          style={{
            position: 'absolute',
            top: -alturaExtra,
            bottom: -alturaExtra,
            right: r,
            width: 1,
            backgroundColor: cor,
            pointerEvents: 'none',
          }}
        />
      ))}
    </>
  );
}

// ⚠️ Abrir a conversa vai por TELEFONE + inbox, que e' o que o `/atendimento` recebe — o mesmo
// cano do botao de WhatsApp da fila ([DA-672]). Nao inventar um parametro de `conversa_id`: seria
// uma segunda porta pra mesma coisa, e so' esta e' testada em producao.
/**
 * O mapa do dia: um quadro por sinal, em dois blocos — o MOVIMENTO (o que ele pediu) e ONDE AGIR.
 * Clicar num quadro fecha o mapa e deixa na lista so' aqueles contatos (escolha dele).
 * ⚠️ Quadro com zero nao filtra: a lista vazia so' repetiria o zero que ja esta escrito.
 */
function ModalMapaDoDia({ aberto, linhas, ativo, onFechar, onEscolher }: {
  aberto: boolean;
  linhas: ContatoDia[];
  ativo: SinalMapa | null;
  onFechar: () => void;
  onEscolher: (s: SinalMapa) => void;
}) {
  // A mesma venda pode aparecer em dois contatos (ficha e numero) — soma por NUMERO, uma vez so'.
  const vendas = new Map<string, number>();
  for (const l of linhas) for (const v of l.vendas ?? []) vendas.set(v.numero, Number(v.total) || 0);
  const totalVendido = [...vendas.values()].reduce((a, b) => a + b, 0);

  const quadro = (s: (typeof SINAIS_MAPA)[number]) => {
    const n = linhas.filter(s.teste).length;
    const selecionado = ativo === s.chave;
    return (
      <button
        key={s.chave}
        type="button"
        disabled={n === 0}
        title={n === 0 ? 'Nenhum contato hoje' : 'Mostrar so estes contatos na lista'}
        onClick={() => onEscolher(s.chave)}
        className="rounded-lg border text-left p-3 disabled:cursor-default"
        style={{
          // O escolhido ganha BORDA (pedido dele): larga e ambar, e o selo "filtrando" abaixo
          // diz o mesmo sem depender de enxergar a cor ([[user_daltonismo]]).
          borderColor: selecionado ? '#f59e0b' : 'var(--border-color, #cbd5e1)',
          borderWidth: selecionado ? 3 : 1,
          cursor: n === 0 ? 'default' : 'pointer',
        }}
      >
        <div className="flex items-baseline gap-2">
          <span className="text-2xl font-bold tabular-nums" style={{ color: 'var(--num-destaque)' }}>{n}</span>
          <span className="font-semibold text-sm">{s.rotulo}</span>
        </div>
        {s.chave === 'comprou' && n > 0 && (
          <div className="text-sm font-semibold tabular-nums">R$ {fmtBRL(totalVendido)}</div>
        )}
        <div className="text-xs mt-1" style={{ color: 'var(--text-muted)' }}>{s.explica}</div>
        {selecionado && (
          <span className="inline-block mt-1 rounded px-1.5 py-0.5 text-[10px] font-bold" style={{ backgroundColor: '#fde68a', color: '#000' }}>
            FILTRANDO A LISTA
          </span>
        )}
      </button>
    );
  };

  return (
    <Modal
      aberto={aberto}
      onFechar={onFechar}
      titulo="Estatistica do dia"
      largura={640}
      compacto
      rodape={<button type="button" className="btn-secundario" onClick={onFechar}>Fechar</button>}
    >
      <div className="text-sm mb-3">
        <strong className="tabular-nums" style={{ color: 'var(--num-destaque)' }}>{linhas.length}</strong>{' '}
        {linhas.length === 1 ? 'contato' : plural('contato')} na lista de hoje.{' '}
        <span style={{ color: 'var(--text-muted)' }}>
          Um contato pode estar em mais de um quadro. Clique num quadro pra ver so aqueles contatos.
        </span>
      </div>
      <div className="text-xs font-bold uppercase mb-1" style={{ color: 'var(--text-muted)' }}>O movimento</div>
      <div className="grid gap-2 mb-4" style={{ gridTemplateColumns: 'repeat(3, minmax(0, 1fr))' }}>
        {SINAIS_MAPA.filter((s) => s.grupo === 'movimento').map(quadro)}
      </div>
      <div className="text-xs font-bold uppercase mb-1" style={{ color: 'var(--text-muted)' }}>Onde agir</div>
      <div className="grid gap-2" style={{ gridTemplateColumns: 'repeat(2, minmax(0, 1fr))' }}>
        {SINAIS_MAPA.filter((s) => s.grupo === 'acao').map(quadro)}
      </div>
    </Modal>
  );
}

export function ContatosDoDia({ onAbrirConversa, sinalRecarga, modo = 'dia' }: {
  onAbrirConversa: (telefone: string, nome: string) => void;
  /** Sobe a cada clique no `Atualizar` da pagina — esta aba carrega sozinha e nao e' alcancada pelo
   *  `carregar()` de fora ([DA-704], mesmo mecanismo da Agenda em [DA-684]). */
  sinalRecarga?: number;
  /** `dia` = o movimento de hoje, escreveu/recebeu/comprou ([DA-691]) · `base` = a base inteira ([DA-705]). */
  modo?: 'dia' | 'base';
}) {
  const naBase = modo === 'base';
  const [linhas, setLinhas] = useState<ContatoDia[]>([]);
  // A janela dos flys vem do servidor (a config da fila), pra dizer "nos ultimos N dias" no lugar
  // de cravar 7 — quem mudar a config muda o texto junto, igual a gaveta da grade ([DA-683]).
  const [dias, setDias] = useState(7);
  const [carregando, setCarregando] = useState(true);
  const [editando, setEditando] = useState<number | null>(null);
  const [gravando, setGravando] = useState<number | null>(null);
  // ── So' o modo `base`: a base nao cabe numa tela, entao ela vem com busca, dois filtros e
  //    paginacao. `busca` e' o que esta DIGITADO e `buscaAtiva` o que foi submetido — sem separar
  //    os dois, cada tecla dispararia uma consulta que abre tres LATERAIS por linha.
  const [busca, setBusca] = useState('');
  const [buscaAtiva, setBuscaAtiva] = useState('');
  const [soSemEtiqueta, setSoSemEtiqueta] = useState(false);
  // O filtro por etiqueta (pedido dele em 25/09/2026). ⚠️ Exclui o "So os sem etiqueta" e vice-versa:
  // juntos dariam sempre zero. E com etiqueta escolhida o servidor ignora a agenda importada — ver
  // o cabecalho da `/contatos-base`.
  const [etiquetaFiltro, setEtiquetaFiltro] = useState<number | null>(null);
  // O total da etiqueta escolhida, pro "SOLAR (46)" do combo. Vem do servidor SEM a busca: buscar
  // um nome nao muda quantos contatos a etiqueta tem. `null` = sem etiqueta ou ainda carregando.
  const [totalEtiqueta, setTotalEtiqueta] = useState<number | null>(null);
  // ✅ Nasce LIGADO desde 15/10/2026 — decisao dele (*"deixe este check marcado como padrao"*),
  //    procurando o Eder, que veio da agenda importada e nao aparecia. Ate ali nascia desligado
  //    ([DA-705]: a importacao de 28/03 era 59% da tela). Medido na troca: a lista vai de 799 pra
  //    1.256 contatos e o "sem etiqueta" de 394 pra 832.
  const [incluirTodos, setIncluirTodos] = useState(true);
  const [pagina, setPagina] = useState(1);
  const [total, setTotal] = useState(0);
  const [totalSemEtiqueta, setTotalSemEtiqueta] = useState(0);
  const [tamanho, setTamanho] = useState(50);
  // Quem BATE na busca e nao entra na lista, por motivo. Sem isto a tela diz "nada bate
  // com X" sobre contato que existe — e, com um resultado na tela, o escondido some sem
  // sinal nenhum (foi assim que buscar "RUAN" achou so' o homonimo).
  const [escondidos, setEscondidos] = useState({ fora_nao_venda: 0, fora_agenda: 0 });
  // ⛔ **Ver os de "Nao sao venda" e' um GESTO, nao um checkbox na barra.** O aviso sozinho
  // nao resolveu: ele leu "1 contato esta fora" e respondeu *"continua nao encontrando"* —
  // contar quem esta escondido nao e' o mesmo que alcancar ([DA-693]: o efeito ja existe, o
  // que falta e' a porta). Nasce DESLIGADO e **volta a desligar a cada busca nova**, senao
  // vira recorte permanente e desfaz calado a decisao da [DA-685].
  const [verNaoVenda, setVerNaoVenda] = useState(false);
  // A foto ampliada ([DA-701], pedido dele) — o lightbox e' o MESMO do chat, nao uma segunda
  // peca: quem ja viu foto de mensagem sabe fechar, girar e baixar sem aprender nada novo.
  const [fotoAberta, setFotoAberta] = useState<{ url: string; nome: string } | null>(null);
  // ⚠️ **A gaveta dos itens da compra** ([DA-703], pedido dele: *"as linhas que possuem vendas
  // poderiam ter um sinal de '+' a esquerda, para clicar e visualizar os produto com as respectivas
  // fotos, assim como ja e' em outros ambientes do sistema"*). O motivo esta no pedido: e' pra
  // decidir a ETIQUETA e o FLY olhando o que a pessoa levou — `CAPACITOR 15UF` diz ar-condicionado
  // e `PP 2X1,5MM` diz eletrica, e nenhuma das duas leituras o sistema sabe fazer (medido: 2.379
  // dos itens vendidos em 30 dias estao SEM categoria, e as categorias que existem sao de
  // fornecedor — `Tramontina Diversos`, `Decorlux PCT` —, nao de perfil de cliente).
  // ⛔ **Lazy load, como o Caixa faz**: sao ~23 linhas por dia e a maioria nunca vai ser aberta;
  // carregar tudo de entrada pagaria N consultas por nada.
  const [abertos, setAbertos] = useState<Set<number>>(new Set());
  // `undefined` = nunca buscou · `null` = buscando · `[]` = sem itens. Os tres estados sao
  // diferentes na tela, e colapsa-los faz "carregando" parecer "nao tem" ([DA-649]).
  const [itensPorVenda, setItensPorVenda] = useState<Record<number, ItemComprado[] | null>>({});

  // ⛔ Reusa `GET /api/caixa/vendas/:id/itens`, a MESMA rota do expand da aba "Vendas hoje" — ela
  // ja le `venda_itens` (o canonico depois de um cancelar+reeditar) e ja devolve a `foto_url`.
  // Escrever uma rota nova aqui repetiria a escolha da tabela, que e' onde mora a armadilha.
  const alternarItens = useCallback((contatoId: number, vendas: VendaDia[]) => {
    setAbertos((atual) => {
      const novo = new Set(atual);
      if (novo.has(contatoId)) { novo.delete(contatoId); return novo; }
      novo.add(contatoId);
      for (const v of vendas) {
        if (v.venda_id == null) continue;
        const id = v.venda_id;
        setItensPorVenda((mapa) => {
          if (mapa[id] !== undefined) return mapa;
          void api.get<{ itens?: ItemComprado[] }>(`/api/caixa/vendas/${id}/itens`)
            .then((r) => setItensPorVenda((m) => ({ ...m, [id]: r.itens ?? [] })))
            .catch(() => setItensPorVenda((m) => ({ ...m, [id]: [] })));
          return { ...mapa, [id]: null };
        });
      }
      return novo;
    });
  }, []);
  // A edicao de nome/sobrenome na propria linha. Guarda o RASCUNHO junto do id, senao trocar de
  // linha com o bloco aberto carregaria o texto digitado na anterior.
  // O TELEFONE entrou no mesmo bloco em 15/10/2026 (pedido dele: *"como eu faco para editar o numero
  // de telefone do contato?"*, olhando o Eder com o numero colado duas vezes). A unica porta era o
  // painel do contato no chat, e quem veio da agenda importada nao tem conversa pra abri-lo.
  // `telefoneOriginal` existe pra so' mandar o numero quando ele MUDOU: trocar numero pergunta ao
  // WhatsApp, e salvar o nome nao pode virar consulta.
  const [editandoNome, setEditandoNome] = useState<{
    id: number; nome: string; sobrenome: string; telefone: string; telefoneOriginal: string; erro: string | null;
  } | null>(null);
  const [gerenciarAberto, setGerenciarAberto] = useState(false);
  const [mapaAberto, setMapaAberto] = useState(false);
  /**
   * O quadro do mapa que filtra a lista. `null` = lista inteira. So' no modo `dia`.
   * ⚠️ Sobrevive a ida ao chat e a volta (pedido dele, 25/09/2026): o uso e' abrir "Sem resposta",
   * responder um por um e voltar — sem isto cada volta caia na lista inteira. `sessionStorage`,
   * como a campanha: e' desta aba do navegador, nao de toda sessao dele.
   */
  const [filtroMapa, setFiltroMapaEstado] = useState<SinalMapa | null>(() => {
    if (modo === 'base') return null;
    try {
      const v = sessionStorage.getItem(CHAVE_FILTRO_MAPA);
      return SINAIS_MAPA.some((s) => s.chave === v) ? (v as SinalMapa) : null;
    } catch { return null; }
  });
  const setFiltroMapa = (s: SinalMapa | null) => {
    setFiltroMapaEstado(s);
    try {
      if (s) sessionStorage.setItem(CHAVE_FILTRO_MAPA, s);
      else sessionStorage.removeItem(CHAVE_FILTRO_MAPA);
    } catch { /* sem storage: o filtro so' nao sobrevive a volta */ }
  };
  // A campanha por etiqueta ([DA-713]) — Fase 2 do disparo de fly.
  // Sobrevive ao F5 (pedido dele, 23/09/2026: *"clicar em atualizar e ele nao sair da tela"*).
  // `sessionStorage` e nao `localStorage`: vale pra ESTA aba do navegador — abrir a Gestao de
  // Vendas noutra aba nao pode nascer com a campanha em cima. So' no modo `base`, que e' o unico
  // que tem o botao.
  const [campanhaAberta, setCampanhaAbertaEstado] = useState(() => {
    try { return modo === 'base' && sessionStorage.getItem(CHAVE_CAMPANHA_ABERTA) === '1'; } catch { return false; }
  });
  const setCampanhaAberta = (aberta: boolean) => {
    setCampanhaAbertaEstado(aberta);
    try {
      if (aberta) sessionStorage.setItem(CHAVE_CAMPANHA_ABERTA, '1');
      else {
        sessionStorage.removeItem(CHAVE_CAMPANHA_ABERTA);
        // Fechar e' largar a campanha que estava sendo montada: o que sobrevive e' o F5, nao isto.
        sessionStorage.removeItem('campanha-fly:produtos');
      }
    } catch { /* sem storage, so' nao lembra */ }
  };
  // ⚠️ **O FLY PELA LINHA** ([DA-707], pedido dele em 10/09/2026: *"para enviar o Fly direto pelo
  // botao, sem precisar abrir o chat"*). Guarda a conversa que a tela do fly vai usar — ela e' a
  // MESMA peca do chat (`ModalEnviarMaterial`), entao previa, arte, limite de 3 e legenda chegam
  // aqui prontos e nao ha uma segunda versao pra divergir.
  const [flyPara, setFlyPara] = useState<{ conversaId: number; contatoId: number; nome: string } | null>(null);
  const [abrindoFly, setAbrindoFly] = useState<number | null>(null);
  const toast = useToast();
  const dialog = useDialog();

  /**
   * Lixeira da linha — pedido dele em 24/09/2026, a mesma da campanha. So' aparece em numero
   * invalido; o resto das regras (sem mensagem, sem orcamento...) quem aplica e' o servidor, e a
   * previa diz o motivo em palavras.
   * ⚠️ Apagou, a linha SAI da lista sem recarregar: recarregar reordenaria e quem esta
   * trabalhando perderia o lugar ([DA-672], o mesmo cuidado do marcar etiqueta).
   */
  async function excluirContato(c: ContatoDia) {
    if (!(await excluirContatoDeVez(c.id, dialog, toast))) return;
    setLinhas((ls) => ls.filter((l) => l.id !== c.id));
    setTotal((t) => Math.max(0, t - 1));
    if (c.sem_etiqueta) setTotalSemEtiqueta((t) => Math.max(0, t - 1));
  }
  // `visiveis` — a etiqueta de vendedora nao entra no seletor ([DA-687]).
  const { visiveis: etiquetasVisiveis, paraCampanha: etiquetasParaCampanha, atualizar: atualizarEtiquetas } = useEtiquetas();

  // ⛔ O provider de etiquetas nasce VAZIO e ate agora so' o `AtendimentoHome` o enchia: sem esta
  // busca o seletor abriria sem uma opcao sequer pra quem entrou direto na Gestao de Vendas — com
  // typecheck verde e o estado vazio desenhado certinho. Alimentar o provider (em vez de guardar
  // numa lista local) mantem uma copia so' e faz o `BadgeEtiqueta` achar cor e apelido no cache.
  useEffect(() => {
    atendimentoApi.listarEtiquetas().then(atualizarEtiquetas).catch(() => {});
  }, [atualizarEtiquetas]);

  const carregar = useCallback(async () => {
    setCarregando(true);
    try {
      if (naBase) {
        const q = new URLSearchParams({ pagina: String(pagina) });
        if (buscaAtiva) q.set('busca', buscaAtiva);
        if (soSemEtiqueta) q.set('sem_etiqueta', '1');
        if (etiquetaFiltro != null) q.set('etiqueta', String(etiquetaFiltro));
        if (incluirTodos) q.set('todos', '1');
        if (verNaoVenda) q.set('nao_venda', '1');
        const r = await api.get<{
          linhas: ContatoDia[]; total: number; total_sem_etiqueta: number; tamanho: number;
          total_etiqueta?: number | null;
          escondidos?: { fora_nao_venda: number; fora_agenda: number };
        }>(`/api/fila-retomada/contatos-base?${q.toString()}`);
        setLinhas(r.linhas);
        setTotalEtiqueta(r.total_etiqueta ?? null);
        setTotal(r.total ?? 0);
        setTotalSemEtiqueta(r.total_sem_etiqueta ?? 0);
        setEscondidos(r.escondidos ?? { fora_nao_venda: 0, fora_agenda: 0 });
        if (r.tamanho) setTamanho(r.tamanho);
      } else {
        const r = await api.get<{ linhas: ContatoDia[]; dias: number }>('/api/fila-retomada/contatos-dia');
        setLinhas(r.linhas);
        if (r.dias) setDias(r.dias);
      }
    } catch (e: any) {
      toast.erro(e?.message ?? (naBase ? 'Falha ao carregar a base de contatos' : 'Falha ao carregar os contatos do dia'));
    } finally {
      setCarregando(false);
    }
  }, [sinalRecarga, toast, naBase, pagina, buscaAtiva, soSemEtiqueta, etiquetaFiltro, incluirTodos, verNaoVenda]);

  useEffect(() => { void carregar(); }, [carregar]);

  /**
   * ⛔ **Manda SEMPRE a lista completa, ocultas inclusive.** O `PUT /contatos/:id/etiquetas`
   * apaga tudo e reinsere o que receber — com so' as visiveis, marcar uma etiqueta aqui apagaria
   * o carimbo do rodizio (a etiqueta da vendedora que atendeu). Medido: os 22 contatos de hoje
   * tem TODOS uma dessas. E' o mesmo cuidado do `PainelContato`.
   *
   * ⚠️ **A linha NAO sai do lugar nem a lista recarrega depois de marcar.** A ordem vem do
   * servidor (sem etiqueta primeiro) e fica congelada enquanto a aba esta aberta: se a linha
   * pulasse pra baixo ao ser marcada, quem esta trabalhando perderia o lugar e veria o proprio
   * trabalho "sumir" — a licao da [DA-672], que com 22 linhas custa mais do que ajuda.
   */
  /**
   * Abre a tela do fly pra um contato da lista ([DA-707]).
   *
   * ⚠️ **Nem toda linha tem conversa** — medido em 10/09/2026: **609 dos 1.138** contatos da caixa
   * Atendimento nunca abriram uma (o capturado no balcao da [DA-703] e' o caso tipico, e e'
   * justamente quem a gente quer alcancar com material). A tela do fly pendura o envio numa
   * conversa, entao aqui ela e' criada na hora se faltar.
   *
   * ⛔ **`atribuir: false`**: criar a conversa NAO e' puxar assunto, e' so' o endereco onde o fly
   * vai morar. Sem isso ela nasceria com dono — a decisao silenciosa que a [DA-702] desfez ao
   * separar "mandar fly" de "atender".
   */
  async function abrirFly(c: ContatoDia) {
    if (abrindoFly) return;
    const nome = nomeDe(c);
    if (c.conversa_id) { setFlyPara({ conversaId: c.conversa_id, contatoId: c.id, nome }); return; }
    if (!c.telefone) return;
    setAbrindoFly(c.id);
    try {
      const conv = await atendimentoApi.novaConversa({
        telefone: c.telefone, inbox: 'atendimento', nome, atribuir: false,
      });
      setFlyPara({ conversaId: conv.id, contatoId: conv.contato_id, nome });
    } catch (e: any) {
      toast.erro(e?.message ?? 'Nao consegui abrir a tela do fly para este contato');
    } finally {
      setAbrindoFly(null);
    }
  }

  async function alternar(c: ContatoDia, etiquetaId: number) {
    const tem = c.etiquetas.some((e) => e.id === etiquetaId);
    const nova = tem
      ? c.etiquetas.filter((e) => e.id !== etiquetaId)
      : [...c.etiquetas, etiquetasVisiveis.find((e) => e.id === etiquetaId)!].filter(Boolean);
    setGravando(c.id);
    try {
      await atendimentoApi.atualizarEtiquetasContato(c.id, nova.map((e) => e.id));
      // ⛔ **A excecao a regra acima: etiqueta de "nao e cliente" TIRA a linha** ([DA-693]).
      // Aqui sumir e' o efeito pedido — e' o gesto inteiro do recurso —, e deixar a linha na
      // tela ate o proximo F5 diria que nao funcionou. O servidor ja nao a devolve mais.
      const virouNaoCliente = nova.some((e) => e.nao_cliente);
      setLinhas((prev) => (virouNaoCliente
        ? prev.filter((l) => l.id !== c.id)
        : prev.map((l) => (l.id === c.id ? { ...l, etiquetas: nova } : l))));
      // ⚠️ **Na base os dois placares vem do SERVIDOR, entao eles nao se mexem sozinhos** — e o
      // numero que a aba existe pra derrubar e' justamente "sem etiqueta". Sem isto, etiquetar 30
      // contatos deixaria o contador parado e o trabalho pareceria nao ter acontecido ([DA-684]:
      // tela que se contradiz com o proprio sucesso ensina a repetir o gesto).
      if (naBase) {
        const tinhaVisivel = c.etiquetas.some((e) => !e.ocultar);
        const temVisivel = nova.some((e) => !e.ocultar);
        if (virouNaoCliente) {
          setTotal((t) => Math.max(0, t - 1));
          if (!tinhaVisivel) setTotalSemEtiqueta((t) => Math.max(0, t - 1));
        } else if (tinhaVisivel !== temVisivel) {
          setTotalSemEtiqueta((t) => Math.max(0, t + (temVisivel ? -1 : 1)));
        }
      }
    } catch {
      toast.erro('Erro ao atualizar etiquetas');
    } finally {
      setGravando(null);
    }
  }

  /**
   * Grava nome e sobrenome do contato ([DA-701], pedido dele em 09/09/2026).
   *
   * ⛔ **A rota e' a MESMA do chat** (`PUT /contatos/:id`, ja usada pelo `PainelContato` e pelo
   * `ModalContatos`): a ficha do contato e' uma so', e uma segunda porta de gravacao seria a
   * chance de as duas divergirem. ⚠️ Manda **so'** nome e sobrenome — o `PUT` faz merge por campo
   * ao contrario do de etiquetas, que substitui o conjunto.
   *
   * ⚠️ **A linha nao sai do lugar**, pelo mesmo motivo do `alternar` acima: quem esta conferindo
   * a lista de cima a baixo perde o lugar se ela se reordenar debaixo do cursor.
   */
  async function gravarNome() {
    if (!editandoNome) return;
    const { id, nome, sobrenome, telefone, telefoneOriginal } = editandoNome;
    const digitosNovo = telefone.replace(/\D/g, '');
    const trocouTelefone = digitosNovo !== telefoneOriginal.replace(/\D/g, '');
    // ⛔ **Nome vazio DIZ por que.** O `nome` e' `string` no contrato (so' o sobrenome aceita nulo),
    // entao a gravacao tem que parar aqui — mas parando CALADA, como o `PainelContato` faz
    // (`if (!nome.trim()) return`), o botao vira beco sem saida. Um aviso custa uma linha.
    if (!nome.trim()) {
      toast.erro('O nome nao pode ficar vazio — o sobrenome pode.');
      return;
    }
    if (trocouTelefone && (digitosNovo.length < 10 || digitosNovo.length > 13)) {
      setEditandoNome({ ...editandoNome, erro: 'Telefone com DDD: 10 ou 11 numeros, como (64) 99999-9999.' });
      return;
    }
    setGravando(id);
    try {
      // O servidor confere o numero novo com o WhatsApp e troca telefone E endereco juntos
      // (`trocarNumeroDoContato`); numero sem WhatsApp volta recusado com o motivo.
      const atualizado = await atendimentoApi.atualizarContato(id, {
        nome: nome.trim(),
        sobrenome: sobrenome.trim() || null,
        ...(trocouTelefone ? { telefone: digitosNovo } : {}),
      });
      setLinhas((prev) => prev.map((l) => (
        l.id === id
          ? {
            ...l, nome: nome.trim(), sobrenome: sobrenome.trim() || null,
            ...(trocouTelefone ? { telefone: atualizado.telefone ?? digitosNovo, numero_invalido: false } : {}),
          }
          : l
      )));
      setEditandoNome(null);
      if (trocouTelefone) toast.sucesso('Telefone trocado — conferido no WhatsApp');
    } catch (e) {
      // Falha fica NA TELA, no bloco aberto, ate a proxima tentativa — nao num toast de 4s.
      setEditandoNome((a) => (a && a.id === id ? { ...a, erro: (e as Error)?.message || 'Erro ao gravar' } : a));
    } finally {
      setGravando(null);
    }
  }

  /**
   * ⚠️ **A busca existe nas DUAS abas** ([DA-705], pedido dele em 10/09/2026), mas o MOTOR e'
   * diferente por uma razao de escala, nao de gosto: na base sao 506 contatos paginados de 50 em
   * 50, entao filtrar so' o que esta na tela acharia apenas dentro da pagina atual — a busca tem
   * que ir ao servidor. No dia sao ~5 a 25 linhas, todas ja carregadas: ali a consulta seria uma
   * ida ao banco pra filtrar o que ja esta na memoria, e o filtro local responde na tecla.
   *
   * ⛔ **Os CAMPOS sao os mesmos nos dois lados** — nome, sobrenome, push_name e telefone —, e isso
   * e' obrigatorio: se a mesma palavra achasse coisas diferentes em cada aba, a busca deixaria de
   * ser confiavel nas duas. Mexeu aqui, mexa no `ILIKE` da rota `contatos-base`.
   */
  const sinalAtivo = !naBase && filtroMapa ? SINAIS_MAPA.find((s) => s.chave === filtroMapa) ?? null : null;
  const linhasVisiveis = (naBase || !buscaAtiva ? linhas : linhas.filter((l) => {
    const alvo = [l.nome, l.sobrenome, l.push_name, l.telefone]
      .map((s) => (s ?? '').trim()).join(' ').toLowerCase();
    return alvo.includes(buscaAtiva.toLowerCase());
  })).filter((l) => !sinalAtivo || sinalAtivo.teste(l));

  const faltam = linhasVisiveis.filter((l) => !l.etiquetas.some((e) => !e.ocultar)).length;

  /**
   * ⚠️ **O que a busca ACHOU e a lista nao mostra.** Pedido dele em 10/09/2026, buscando o
   * proprio nome: *"por que quando pesquiso meu nome, a busca nao encontra?"* — e o contato
   * existe, com 183 mensagens; esta fora por ser numero da EQUIPE em "Nao sao venda". A tela
   * respondia *"nada bate com RUAN"*, que e' fato fabricado ([DA-707] p.8, o mesmo `Nunca`).
   * ⛔ **Nao vale so' no vazio**: a primeira busca dele devolveu o homonimo `Thauany Ruan` e o
   * escondido sumiu sem sinal nenhum. Por isso o aviso mora no CONTADOR, que esta sempre na
   * tela, e o vazio so' o repete.
   * ⚠️ O aviso DIZ o motivo, porque os dois tem portas diferentes: "Nao sao venda" se desfaz
   * no botao do topo, a agenda importada no checkbox ao lado da busca.
   */
  const escondidosTotal = escondidos.fora_nao_venda + escondidos.fora_agenda;
  const nomeEtiquetaFiltro = naBase && etiquetaFiltro != null
    ? (etiquetasVisiveis.find((e) => e.id === etiquetaFiltro)?.nome ?? null)
    : null;
  /** O aviso vira AÇÃO: quem esta fora por "Nao sao venda" se alcanca aqui, num clique. */
  const botaoVerNaoVenda = naBase && buscaAtiva && escondidos.fora_nao_venda > 0 ? (
    <button
      type="button"
      className="underline"
      style={{ color: 'var(--num-destaque)' }}
      onClick={() => { setPagina(1); setVerNaoVenda(true); }}
    >
      mostrar mesmo assim
    </button>
  ) : null;
  const avisoEscondidos = naBase && buscaAtiva && escondidosTotal > 0
    ? [
        escondidosTotal === 1
          ? '1 contato bate com a busca e esta fora da lista: '
          : `${escondidosTotal} contatos batem com a busca e estao fora da lista: `,
        [
          escondidos.fora_nao_venda > 0 ? `${escondidos.fora_nao_venda} em "Nao sao venda"` : null,
          escondidos.fora_agenda > 0 ? `${escondidos.fora_agenda} na agenda importada` : null,
        ].filter(Boolean).join(' e '),
        '.',
      ].join('')
    : null;
  /** ⚠️ Ligada a vista, a tela DIZ que esta ligada e como voltar — recorte silencioso faz
      o proximo numero da tela mentir sem ninguem saber por que. */
  const avisoVerNaoVenda = naBase && verNaoVenda ? (
    <>
      Incluindo nesta busca quem esta em "Nao sao venda".{' '}
      <button
        type="button"
        className="underline"
        style={{ color: 'var(--num-destaque)' }}
        onClick={() => { setPagina(1); setVerNaoVenda(false); }}
      >
        voltar a esconder
      </button>
    </>
  ) : null;
  const linhaBusca = avisoVerNaoVenda ?? (avisoEscondidos ? (
    <>{avisoEscondidos} {botaoVerNaoVenda}</>
  ) : null);

  return (
    <div>
      <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
        {/* O placar mora aqui, e nao no rotulo da aba: ele conta CONTATO, e as outras abas contam
            orcamento. Cada numero visivel na tela tem que dizer de quem ele e' ([DA-597]). */}
        <div className="text-sm">
          {carregando ? (
            <span style={{ color: 'var(--text-muted)' }}>Carregando...</span>
          ) : (
            <>
              {/* ⚠️ `contar()` JA devolve o numero junto ("22 contatos") — usa-lo aqui ao lado do
                  numero destacado imprimiria "22 22 contatos". Como o destaque e' so' do NUMERO,
                  a palavra vem do `plural()`, que e' a outra metade do mesmo helper. */}
              {/* ⚠️ **Na base o placar conta a BASE, nao a pagina.** `linhas.length` seria sempre
                  50 e o numero mais importante da tela — quantos faltam etiquetar — viraria "quantos
                  faltam nesta pagina", que nao serve pra nada. Os dois totais vem do servidor, da
                  mesma consulta que montou a pagina. */}
              <strong className="tabular-nums" style={{ color: 'var(--num-destaque)' }}>
                {naBase ? total : linhasVisiveis.length}
              </strong>{' '}
              {(naBase ? total : linhasVisiveis.length) === 1 ? 'contato' : plural('contato')}
              {naBase ? '' : ' hoje'} ·{' '}
              <strong className="tabular-nums" style={{ color: 'var(--num-destaque)' }}>
                {naBase ? totalSemEtiqueta : faltam}
              </strong>{' '}
              sem etiqueta
            </>
          )}
          {/* ⚠️ Com a lista VAZIA quem diz e' a caixa do meio — as duas juntas imprimiam a
              mesma frase duas vezes na mesma tela. Medido na tela publicada. */}
          {!carregando && linhaBusca && linhasVisiveis.length > 0 && (
            <div className="mt-0.5 text-xs" style={{ color: 'var(--text-muted)' }}>
              {linhaBusca}
            </div>
          )}
          {/* ⚠️ Filtro do mapa ligado se ANUNCIA, com a volta ao lado: sem isto "6 contatos hoje"
              se leria como o dia inteiro, e a lista completa pareceria ter sumido. */}
          {!carregando && sinalAtivo && (
            <div className="mt-1 text-xs flex items-center gap-2">
              <span
                className="rounded px-2 py-0.5 font-semibold"
                style={{ backgroundColor: '#fde68a', color: '#000' }}
              >
                Mostrando so: {sinalAtivo.rotulo}
              </span>
              {/* Volta ao mapa com o quadro escolhido marcado (pedido dele, 25/09/2026). Nasceram
                  como links sublinhados; ele pediu "mais destacados e separados" — botao SOLIDO,
                  como toda acao que ele achou discreta (§ UI do sistema/CLAUDE.md). */}
              <button
                type="button"
                className="ml-2 inline-flex items-center gap-1 rounded px-3 py-1 text-[12px] font-semibold bg-blue-600 text-white hover:bg-blue-700"
                style={{ whiteSpace: 'nowrap' }}
                onClick={() => setMapaAberto(true)}
              >
                <BarChart3 size={14} /> Voltar ao mapa
              </button>
              <button
                type="button"
                className="ml-2 inline-flex items-center gap-1 rounded px-3 py-1 text-[12px] font-semibold bg-blue-600 text-white hover:bg-blue-700"
                style={{ whiteSpace: 'nowrap' }}
                onClick={() => setFiltroMapa(null)}
              >
                <List size={14} /> Mostrar todos
              </button>
            </div>
          )}
        </div>
        {/* ⚠️ **Uma porta so' pra tela inteira**, escolha dele em 09/09/2026 (*"um botao Gerenciar,
            ao lado de Etiqueta, para poder gerenciar as Etiquetas, sem sair da tela"*) — o modal e'
            o MESMO de Configuracoes, ja reusado pela ficha do contato. Por linha seriam 11 botoes
            abrindo a mesma coisa. */}
        <span className="flex items-center gap-2">
          {/* ⚠️ So' na aba `Contatos` (a base): a campanha dispara por ETIQUETA, e na aba do dia
              o recorte e' o proprio dia — um botao de campanha ali seria controle sem efeito.
              ⛔ `btn-primario` de proposito: e' a unica porta pra campanha, e `btn-secundario`
              sozinho num cabecalho vira parte do titulo ([DA-667] — dois meses invisivel). */}
          {naBase && (
            <button
              type="button"
              onClick={() => setCampanhaAberta(true)}
              className="btn-primario flex items-center gap-1 text-sm"
            >
              <Megaphone size={14} /> Disparar campanha
            </button>
          )}
          {/* O MAPA DO DIA (pedido dele em 25/09/2026, com o lugar marcado no print: a esquerda do
              Gerenciar). So' no dia: na base nao ha "hoje" pra resumir. */}
          {!naBase && (
            <button
              type="button"
              onClick={() => setMapaAberto(true)}
              className="btn-secundario flex items-center gap-1 text-sm"
            >
              <BarChart3 size={14} /> Estatistica
            </button>
          )}
          <button type="button" onClick={() => setGerenciarAberto(true)} className="btn-secundario flex items-center gap-1 text-sm">
            <Tag size={14} /> Gerenciar etiquetas
          </button>
          <button type="button" onClick={() => void carregar()} className="btn-secundario flex items-center gap-1 text-sm">
            <RefreshCw size={14} /> Atualizar
          </button>
        </span>
      </div>

      {/* ── A BARRA DA BASE ([DA-705]) — busca, os dois recortes e a paginacao. So' no modo `base`:
             no dia sao ~20 linhas e nada disso teria o que filtrar.
          ⚠️ **A busca e' por SUBMISSAO (Enter ou lupa), nao por tecla digitada**: cada consulta
             abre tres LATERAIS por linha (ultima compra, ultimo fly, etiquetas), e disparar isso a
             cada caractere derrubaria a tela em cima do banco de producao. */}
      {/* ⚠️ **A BUSCA vale nas DUAS abas** ([DA-705], pedido dele em 10/09/2026) — os CHECKBOXES,
          nao. "So os sem etiqueta" e "Incluir a agenda importada" governam o RECORTE da base, e no
          dia o recorte e' o proprio dia: ali eles seriam controle sem efeito, que e' pior que
          controle nenhum. A regra que ele fechou e' sobre COLUNA — toda coluna de uma existe na
          outra —, e ela nao obriga a repetir filtro que nao filtra nada. */}
      <div className="flex items-center gap-3 mb-3 flex-wrap text-sm">
          <form
            onSubmit={(ev) => { ev.preventDefault(); setPagina(1); setVerNaoVenda(false); setBuscaAtiva(busca.trim()); }}
            className="flex items-center gap-1"
          >
            {/* Molde copiado do `ModalContatos`, que ja e' a busca de contato da casa — inclusive
                o `fontSize: 16px`, que e' o que impede o iOS de dar zoom ao focar o campo.
                ⚠️ `no-upper` porque o uppercase global e' pra cadastro que GRAVA; aqui e' filtro. */}
            <div className="relative" style={{ width: 240 }}>
              <Search size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 opacity-50" />
              <input
                type="text"
                value={busca}
                onChange={(ev) => setBusca(ev.target.value)}
                placeholder="Nome ou telefone..."
                className="w-full pl-8 pr-3 py-1.5 rounded border text-sm outline-none focus:ring-2 focus:ring-blue-400 no-upper"
                style={{ borderColor: 'var(--border, rgba(0,0,0,0.15))', fontSize: '16px' }}
              />
            </div>
            <button type="submit" className="btn-secundario flex items-center gap-1">
              Buscar
            </button>
            {/* Aparece so' quando ha busca ATIVA: botao de limpar o que ja esta vazio e' ruido. */}
            {buscaAtiva && (
              <button
                type="button"
                className="btn-secundario"
                onClick={() => { setBusca(''); setBuscaAtiva(''); setPagina(1); setVerNaoVenda(false); }}
              >
                Limpar
              </button>
            )}
          </form>

          {naBase && (
            <label className="flex items-center gap-1.5 cursor-pointer">
              <input
                type="checkbox"
                checked={soSemEtiqueta}
                onChange={(ev) => {
                  setSoSemEtiqueta(ev.target.checked);
                  if (ev.target.checked) setEtiquetaFiltro(null);
                  setPagina(1);
                }}
              />
              So os sem etiqueta
            </label>
          )}

          {/* O combo de etiqueta (pedido dele em 25/09/2026). ⛔ `paraCampanha` — a mesma lista
              do combo de campanha: sem a da vendedora (oculta, [DA-687]; quem atendeu ja tem a
              coluna `Atendeu`) e sem as `nao_cliente` (decisao dele no mesmo dia: *"Nao e' cliente
              nao deve aparecer no combo"*). */}
          {naBase && (
            <div style={{ width: 220 }}>
              <ComboBox
                // O total entre parenteses so' na ESCOLHIDA (pedido dele em 25/09/2026) — e so'
                // depois de chegar: trocando de etiqueta, o numero da anterior nao aparece na nova.
                opcoes={etiquetasParaCampanha.map((e) => ({
                  value: e.id,
                  label: e.id === etiquetaFiltro && totalEtiqueta != null ? `${e.nome} (${totalEtiqueta})` : e.nome,
                }))}
                value={etiquetaFiltro}
                onChange={(v) => {
                  const id = v === '' ? null : Number(v);
                  setTotalEtiqueta(null);
                  setEtiquetaFiltro(id);
                  if (id != null) setSoSemEtiqueta(false);
                  setPagina(1);
                }}
                placeholder="Filtrar por etiqueta..."
              />
            </div>
          )}

          {/* ⚠️ O rotulo DIZ o que entra, porque "mostrar todos" nao contaria a ninguem que sao
              612 contatos de uma agenda de aparelho importada de uma vez — gente que nunca falou
              com a loja e nunca comprou. ⛔ Quem so' RECEBEU, e o capturado no balcao, ja estao na
              lista sem este clique: eles tem relacao com a loja, so' nao escreveram ([DA-705]). */}
          {naBase && (
            <label
              className={`flex items-center gap-1.5 ${etiquetaFiltro != null ? 'opacity-60' : 'cursor-pointer'}`}
              title="Os 612 contatos da agenda importada em 28/03/2026: nunca escreveram, nunca receberam e nunca compraram."
            >
              {/* ⚠️ Com etiqueta escolhida o servidor ja traz a agenda junto — a caixa fica
                  travada e DIZ por que, em vez de ser controle sem efeito. */}
              <input
                type="checkbox"
                checked={incluirTodos || etiquetaFiltro != null}
                disabled={etiquetaFiltro != null}
                onChange={(ev) => { setIncluirTodos(ev.target.checked); setPagina(1); }}
              />
              Incluir a agenda importada
              {etiquetaFiltro != null && (
                <span className="text-xs" style={{ color: 'var(--text-muted)' }}>(a etiqueta ja traz todos)</span>
              )}
            </label>
          )}
        </div>

      {/* ⛔ **Fechar o Gerenciar recarrega a lista.** Renomear, trocar a cor ou excluir uma etiqueta
          la' dentro muda o que as pills desta tela dizem — e elas vem do `GET /contatos-dia`, nao do
          cache do contexto ([DA-684]: quem desenha o dado tem que se reler). Sem isso a linha
          seguiria exibindo o nome velho, ou uma etiqueta que nao existe mais. */}
      <ModalEtiquetas aberto={gerenciarAberto} onFechar={() => { setGerenciarAberto(false); void carregar(); }} />

      {!naBase && (
        <ModalMapaDoDia
          aberto={mapaAberto}
          linhas={linhas}
          ativo={filtroMapa}
          onFechar={() => setMapaAberto(false)}
          onEscolher={(s) => { setFiltroMapa(s); setMapaAberto(false); }}
        />
      )}

      {/* A campanha le as etiquetas do MESMO contexto que a lista usa, entao o que ele acabou de
          criar no Gerenciar ja aparece no seletor sem recarregar a tela.
          ⛔ `paraCampanha`, nunca `visiveis`: a etiqueta `nao_cliente` aparece no seletor do
          contato (e' por la que ele a aplica) e NAO pode ser alvo de campanha — medido, as tres
          dao zero elegiveis. */}
      {campanhaAberta && (
        <ModalCampanhaFly etiquetas={etiquetasParaCampanha} onFechar={() => { setCampanhaAberta(false); void carregar(); }} />
      )}

      {/* ⚠️ **A MESMA tela do chat** ([DA-707]) — nao ha copia daqui. O que muda e' so' de onde ela
          e' aberta; previa, arte 1080x1080, limite de 3 produtos, legenda e a regra do link `/p/`
          chegam prontos ([DA-681]/[DA-682]).
          ⚠️ **Recarrega a lista ao enviar**: a coluna `Flys` desta mesma tela mostra o que foi
          mandado ao contato ([DA-683]), entao sem isto ela ficaria mentindo ate o proximo F5 —
          e' a [DA-684] (quem desenha o dado tem que se reler). */}
      {flyPara && (
        <ModalEnviarMaterial
          conversaId={flyPara.conversaId}
          nomeContato={flyPara.nome}
          contatoId={flyPara.contatoId}
          // O bloco SOBRE O CLIENTE grava nome e etiqueta mesmo sem enviar — fechar rele a lista.
          // (o envio chama `onClose` logo depois, entao o recarregar de la' cobre os dois casos)
          onClose={() => { setFlyPara(null); void carregar(); }}
        />
      )}

      {carregando ? (
        <div className="text-center text-gray-400 py-12 text-sm">Carregando...</div>
      ) : linhasVisiveis.length === 0 ? (
        /* ⚠️ **O vazio DIZ por que esta vazio**, e na base sao tres motivos diferentes — busca sem
            resultado, filtro que zerou (que aqui e' BOA noticia) e base vazia. Um texto so' pros
            tres faria o operador procurar defeito onde ha sucesso. */
        <div className="rounded-xl border border-gray-200 text-center py-12 px-6" style={{ backgroundColor: 'var(--panel-bg)' }}>
          {/* ⛔ **A busca vem PRIMEIRO no ternario, e isso nao e' ordem a toa**: desde que ela
              existe nas duas abas ([DA-705]), buscar algo que nao existe no dia cairia no texto
              "Ninguem escreveu ainda hoje" — que e' FALSO com 5 contatos na lista e manda o
              operador procurar defeito onde ha so' um filtro ligado. */}
          {buscaAtiva ? (
            <>
              {/* ⛔ Com alguem escondido, o titulo NAO pode dizer "nenhum encontrado": a busca
                  encontrou, quem nao mostra e' o recorte da aba. */}
              <div className="font-semibold mb-1">
                {avisoEscondidos ? 'Nenhum contato nesta lista' : 'Nenhum contato encontrado'}
              </div>
              <div className="text-sm" style={{ color: 'var(--text-muted)' }}>
                {linhaBusca ?? (
                  <>
                    Nada bate com <strong>{buscaAtiva}</strong> em nome, sobrenome ou telefone
                    {naBase ? '' : ' entre os contatos de hoje'}
                    {nomeEtiquetaFiltro ? <> entre os da etiqueta <strong>{nomeEtiquetaFiltro}</strong></> : ''}.
                  </>
                )}
              </div>
            </>
          ) : sinalAtivo ? (
            <>
              <div className="font-semibold mb-1">Nenhum contato hoje em "{sinalAtivo.rotulo}"</div>
              <div className="text-sm" style={{ color: 'var(--text-muted)' }}>
                <button type="button" className="underline" style={{ color: 'var(--num-destaque)' }} onClick={() => setFiltroMapa(null)}>
                  mostrar todos
                </button>
              </div>
            </>
          ) : !naBase ? (
            <>
              <div className="font-semibold mb-1">Nenhum movimento ainda hoje</div>
              <div className="text-sm" style={{ color: 'var(--text-muted)' }}>
                A lista se enche sozinha: entra quem escrever pra caixa Atendimento, quem receber
                mensagem nossa e quem comprar.
              </div>
            </>
          ) : nomeEtiquetaFiltro ? (
            <>
              <div className="font-semibold mb-1">Nenhum contato com a etiqueta {nomeEtiquetaFiltro}</div>
              <div className="text-sm" style={{ color: 'var(--text-muted)' }}>
                Limpe o filtro de etiqueta pra ver a base inteira.
              </div>
            </>
          ) : soSemEtiqueta ? (
            <>
              <div className="font-semibold mb-1">Todos os contatos estao etiquetados</div>
              <div className="text-sm" style={{ color: 'var(--text-muted)' }}>
                Nao ha o que fazer neste recorte — desmarque o filtro pra ver a base inteira.
              </div>
            </>
          ) : (
            <>
              <div className="font-semibold mb-1">Nenhum contato na base</div>
              <div className="text-sm" style={{ color: 'var(--text-muted)' }}>
                A lista se enche conforme as mensagens chegam na caixa Atendimento.
              </div>
            </>
          )}
        </div>
      ) : (
        <div className="flex flex-col gap-2">
          {/* ⚠️ **O CABECALHO DAS COLUNAS** ([DA-707], pedido dele em 10/09/2026 com o retangulo
              vermelho exatamente nesta faixa: *"vamos criar aqui um cabecalho, para identificar
              cada coluna. Este cabecalho deve fixo, de forma que role a tela e ele nao suma"*).
              Ate aqui a lista tinha nove colunas e nenhum titulo — quem nao construiu a tela
              precisava deduzir o que cada numero e' pela forma dele.
              ⛔ **Ele entra nas DUAS abas de graca**, porque a peca e' uma so' ([DA-705]) — o que
              muda por modo e' o que o rotulo DIZ, do mesmo jeito que a celula: `Compra` vira
              `Ultima compra`, `Flys` vira `Ultimo fly`.
              ⚠️ **Cores no par CASADO `--grid-header-bg` + `--grid-header-fg`** ([DA-678]:
              misturar variaveis de familias diferentes nao garante contraste entre elas), que e'
              o mesmo par do `<thead>` das outras abas — nada novo pra aprender.
              ⚠️ **A borda existe pra ALINHAR, nao pra aparecer**: os cards tem `border` de 1px, e
              sem ela aqui o rotulo ficaria 1px fora do prumo da coluna que ele nomeia.
              ⚠️ `sticky top-0` cola no topo do `.app-content`, que e' quem rola (`overflow-y:
              auto`); a faixa de avisos e o menu ficam por fora dele e nao entram na conta.
              ⛔ **`top` e' -20px e nao 0** — os 20px sao o `padding` do `.app-content`. Medido na
              tela publicada: com `top: 0` o cabecalho parava 20px abaixo da borda visivel e as
              linhas passavam por essa fresta, aparecendo ACIMA do titulo delas (o retrato mostrava
              meia linha por cima do cabecalho). O negativo cola no topo do que se VE, e nada e'
              cortado: o recorte do rolamento e' a borda do painel, nao a do recuo — foi por isso
              que a fresta mostrava linha. ⚠️ Numero copiado do `padding` em `styles.css`; se ele
              mudar la, muda aqui. */}
          <div
            className="sticky z-10 rounded-xl border px-3 py-1.5 text-[11px] font-semibold leading-tight"
            style={{
              top: -20,
              backgroundColor: 'var(--grid-header-bg)',
              color: 'var(--grid-header-fg)',
              borderColor: 'var(--grid-header-bg)',
            }}
          >
            <div className="flex items-center gap-3 relative">
              {/* ⚠️ No cabecalho o risco e' CLARO e fixo: o fundo `--grid-header-bg` e' escuro nos
                  quatro temas da casa (#1c1410, #0f1218, #091020, #11224a), entao aqui nao ha o
                  problema de par que o `--separador-coluna` resolve nas linhas. */}
              <RiscosDeColuna cor="rgba(255,255,255,0.28)" alturaExtra={6} />
              {/* Sem titulo: o cabecalho nomeia o que a coluna CONTEM, e aqui o conteudo e' o
                  gesto de abrir a gaveta dos itens — mesma escolha do `<thead>` da fila. */}
              <div className="flex-shrink-0" style={{ width: COL.abrir }} />
              <div className="flex-shrink-0" style={{ width: COL.avatar }} />
              <div className="min-w-0 flex-1">Nome</div>
              <div className="flex-shrink-0" style={{ width: COL.sobrenome }}>Sobrenome</div>
              <div className="flex-shrink-0" style={{ width: COL.lapis }} />
              <div className="flex-shrink-0" style={{ width: COL.atendeu }}>Atendeu</div>
              {/* ⚠️ Larguras MEDIDAS na tela publicada com a sonda da [DA-705] (`span` absoluto com
                  a fonte real, `getBoundingClientRect`), nao arredondadas no olho: `Últ. envio`
                  pede **50px** dos 58, `Recebidas` **56** dos 58 e `Última compra` **77** dos 176. */}
              <div className="flex-shrink-0" style={{ width: COL.compra }}>
                {naBase ? 'Última compra' : 'Compra'}
              </div>
              <div className="flex-shrink-0" style={{ width: COL.flys }}>
                {naBase ? 'Último fly' : 'Flys'}
              </div>
              {/* O `paddingLeft` copia o `px-1` do botao da coluna: sem ele o rotulo fica 4px a
                  esquerda das pills que ele nomeia. */}
              <div className="flex-shrink-0" style={{ width: COL.etiquetas, paddingLeft: 4 }}>
                Etiquetas
              </div>
              <div className="flex-shrink-0" style={{ width: COL.chat }} />
              <div className="flex-shrink-0" style={{ width: COL.origem }}>Origem</div>
              {/* ⚠️ **`Últ. envio` mora AQUI desde 10/09/2026**, colado em `Recebidas` (pedido
                  dele, com a seta no print: *"vai fazer mais sentido"*). As duas colunas respondem
                  a mesma pergunta em direcoes opostas — o que a loja MANDOU e o que o contato
                  ESCREVEU —, e separadas por meia tela a comparacao exigia varrer a linha inteira.
                  ⚠️ **`Envio` e nao `Ult. msg`**: vizinhas e ambas sobre mensagem, dois rotulos
                  parecidos em cima de numeros diferentes fariam o operador ler um pelo outro; o
                  que as separa e' a direcao, entao e' a direcao que vai no nome. */}
              <div
                className="flex-shrink-0 text-right"
                style={{ width: COL.ultEnvio }}
                title="Quando a loja enviou a ultima mensagem para este contato"
              >
                Últ. envio
              </div>
              {/* ⚠️ A celula embaixo tem DUAS linhas com alcances diferentes por modo — no dia, a
                  hora da PRIMEIRA mensagem dele e quantas chegaram; na base, a DATA da ultima e o
                  total de sempre. O rotulo diz o que as duas tem em comum (mensagem RECEBIDA) e o
                  `title` conta o resto, que nao cabe em 58px. */}
              <div
                className="flex-shrink-0 text-right"
                style={{ width: COL.recebidas }}
                title={naBase
                  ? 'Data da ultima mensagem que este contato enviou, e o total que ja enviou'
                  : 'Hora da primeira mensagem que ele enviou hoje, e quantas enviou no dia'}
              >
                Recebidas
              </div>
            </div>
          </div>
          {linhasVisiveis.map((c) => {
            const visiveisDoContato = c.etiquetas.filter((e) => !e.ocultar);
            const aberto = editando === c.id;
            // ⚠️ **Quem cuidou do contato**, e desde a [DA-703] ele nem sempre e' o do CHAT: a aba
            // passou a listar quem so' COMPROU, e essas linhas nao tem atendente nenhum. Ai quem
            // cuidou e' o VENDEDOR — e ele sobe pra coluna do meio, que estaria vazia.
            // ⛔ So' quando ha UM vendedor no dia: com mais de um (o DEVAIR comprou 4 vezes, com 3
            // pessoas diferentes) nao existe "o" vendedor, e cravar um mentiria sobre os outros.
            // ⚠️ Sem isto o layout QUEBRA, e foi medido na tela publicada: com o atendente nulo,
            // todo vendedor virava "outro" e ia colado no orcamento — `ORC-007659 · CRISTINA` pede
            // **135px** dos 176 da celula, e o que sobrava espremia o TOTAL ate ele quebrar em duas
            // linhas (`R$` em cima, `22,50` embaixo). E' a [DA-700] p.4 de novo.
            const vendedoresDoDia = [...new Set((c.vendas ?? []).map((v) => v.vendedor).filter(Boolean))];
            const quemCuidou = c.atendente ?? (vendedoresDoDia.length === 1 ? vendedoresDoDia[0]! : null);
            const vendas = c.vendas ?? [];
            const temItens = vendas.some((v) => v.venda_id != null);
            const itensAbertos = abertos.has(c.id);
            return (
              <div
                key={c.id}
                className="rounded-xl border border-gray-200 px-3 py-2"
                /* ⚠️ **Toda informacao do registro sai PRETA** ([DA-707], pedido dele em
                   10/09/2026: *"todas as informacoes do registro devem ser na cor preta"*). O
                   cinza `--text-muted` governava metade da linha — telefone, quem atendeu, numero
                   do orcamento, origem, contagem de mensagens — e nesta aba nada disso e' apoio:
                   e' o dado que ele veio ler.
                   ⛔ **Vai no par CASADO `--registro-fg`, nao no `#000` cravado** ([DA-678]): nos
                   temas escuros o painel e' `#0d1422`, e preto sobre ele da 1,1:1 — invisivel. O
                   token e' preto puro nos temas claros (15,6:1 sobre o painel do Azul Royal) e
                   vira a cor do painel no escuro.
                   ⚠️ Herda pra dentro: quem precisa de outra cor a declara (as pills de etiqueta,
                   o selo do fly, o cabecalho da gaveta dos itens). */
                style={{ backgroundColor: 'var(--panel-bg)', color: 'var(--registro-fg)' }}
              >
                <div className="flex items-center gap-3 relative">
                  <RiscosDeColuna cor="var(--separador-coluna)" alturaExtra={8} />
                  {/* ⛔ **O `+` fica A ESQUERDA de tudo, e o espaco existe ate quando nao ha venda**
                      ([DA-703], pedido dele). Molde copiado do expand da aba "Vendas hoje"
                      (`CaixaPainel`), inclusive o inverter de cor quando aberto — quem ja usa aquela
                      tela nao aprende nada novo. ⚠️ Elemento condicional no COMECO da linha deslocaria
                      o avatar e o nome nas linhas sem venda, e a lista perderia o prumo: por isso o
                      lugar e' reservado sempre, e so' o botao e' que falta. */}
                  <div className="flex-shrink-0" style={{ width: COL.abrir }}>
                    {temItens && (
                      <button
                        type="button"
                        onClick={() => alternarItens(c.id, vendas)}
                        title={itensAbertos ? 'Ocultar os itens da compra' : 'Ver os itens da compra'}
                        aria-expanded={itensAbertos}
                        className="flex items-center justify-center rounded"
                        style={{
                          width: 22, height: 22, padding: 0,
                          border: '1px solid var(--input-border, #d1d5db)',
                          background: itensAbertos ? 'var(--accent, #2563eb)' : 'transparent',
                          color: itensAbertos ? '#fff' : 'var(--accent, #2563eb)',
                          cursor: 'pointer',
                        }}
                      >
                        {itensAbertos ? <Minus size={12} /> : <Plus size={12} />}
                      </button>
                    )}
                  </div>

                  {/* ⚠️ **Clicar amplia a foto** ([DA-701], pedido dele). O `onFotoClick` ja existia
                      no `Avatar` e so' vale quando ha FOTO NA TELA: se a URL do WhatsApp expirou, o
                      componente ja caiu nas iniciais e o clique morre junto, em vez de abrir um
                      quadro quebrado — dois dos 16 contatos de hoje estao nesse caso. */}
                  <Avatar
                    nome={nomeDe(c)}
                    fotoUrl={c.foto_url}
                    tamanho={COL.avatar}
                    onFotoClick={c.foto_url ? () => setFotoAberta({ url: c.foto_url!, nome: nomeDe(c) }) : undefined}
                  />

                  {/* NOME — o unico `flex-1` da linha, entao e' ele que paga toda coluna nova.
                      Medido em 10/09/2026 antes de partir a coluna: em 1920 o bloco tem 673px e
                      fica com 587 depois de ceder 110 pro Sobrenome e receber de volta os 32 do
                      lapis que saiu do fim da linha — os 50 nomes da pagina cabem ([DA-705]). */}
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-semibold truncate">{soNome(c)}</span>
                      {c.telefone && (
                        <span className="text-xs tabular-nums">
                          {formatTelefoneBR(c.telefone)}
                        </span>
                      )}
                    </div>
                    {c.cliente_nome && (
                      <div className="text-[11px] font-semibold truncate">
                        {c.cliente_nome}
                      </div>
                    )}
                  </div>

                  {/* SOBRENOME — coluna propria ([DA-705], pedido dele). Largura FIXA em pixel, como
                      todas as vizinhas: e' o que mantem as colunas de baixo alinhadas linha a linha.
                      ⚠️ 110px foi MEDIDO, nao chutado: o pior sobrenome real da base
                      (`Queiroz Jesus Salva`) pede 136px e trunca; o tipico (`Melo`) pede 34. Dar os
                      136 gastaria 26px a mais do nome em TODAS as linhas pra caber um unico caso —
                      e o `truncate` ja contem esse caso sem quebrar o alinhamento. */}
                  <div
                    className="flex-shrink-0 font-semibold truncate text-sm"
                    style={{ width: COL.sobrenome }}
                    title={soSobrenome(c) || undefined}
                  >
                    {soSobrenome(c)}
                  </div>

                  {/* ⚠️ **O lapis mora AQUI desde 10/09/2026** ([DA-705], pedido dele: *"o botao
                      Editar no final da linha do registro passa para ficar ao lado do campo
                      Sobrenome"*). Ele ja editava os DOIS campos ([DA-701]); o que mudou e' o lugar,
                      e o lugar diz o que ele faz — no fim da linha, depois da hora e das etiquetas,
                      nao havia como saber que aquele lapis era do nome.
                      ⛔ Abrir a edicao FECHA o seletor de etiquetas (e vice-versa): os dois pousam
                      no mesmo lugar embaixo da linha, e dois blocos abertos empilhariam. */}
                  <div className="flex-shrink-0" style={{ width: COL.lapis }}>
                    <button
                      type="button"
                      onClick={() => {
                        setEditando(null);
                        setEditandoNome(editandoNome?.id === c.id
                          ? null
                          : {
                            id: c.id, nome: c.nome ?? '', sobrenome: c.sobrenome ?? '',
                            // Numero quebrado aparece CRU: formatado, a mascara esconderia o defeito.
                            telefone: c.numero_invalido ? (c.telefone ?? '') : formatTelefoneBR(c.telefone ?? ''),
                            telefoneOriginal: c.telefone ?? '',
                            erro: null,
                          });
                      }}
                      className="btn-icon"
                      title="Editar nome, sobrenome e telefone"
                    >
                      <Pencil size={16} />
                    </button>
                  </div>

                  {/* ⚠️ **QUEM ATENDEU** — pedido dele em 09/09/2026 (*"Tem como colocar o usuario
                      que atendeu ao Contato no chat?"*), com o retangulo vermelho justamente nesta
                      coluna VAZIA nas linhas sem venda: o que ele quer aqui e' quem esta cuidando do
                      contato, e isso TODA linha tem (12 de 12 no dia da medicao).
                      ⛔ **Nao virou uma segunda coluna ao lado do vendedor.** Medido em 30 dias: das
                      55 vendas ligadas a contato do dia, **52 tem o mesmo nome nos dois papeis**;
                      duas colunas imprimiriam "CRISTINA CRISTINA" lado a lado quase sempre — o
                      mesmo ruido que o chat ja teve de desfazer. O vendedor aparece so' quando for
                      OUTRA pessoa, e ai vai colado no orcamento (bloco de baixo), que e' o que ele
                      qualifica.
                      ⛔ Largura FIXA em PIXEL — a raiz do app e' 14px e o `w-*` do Tailwind e' rem;
                      e e' o que mantem a coluna das etiquetas alinhada. */}
                  <div className="flex-shrink-0 text-[11px] font-semibold leading-tight truncate" style={{ width: COL.atendeu }}>
                    {quemCuidou ?? ''}
                  </div>

                  {/* ⛔ **Tamanho em PIXEL nos dois, e o TOTAL maior que o numero do orcamento.**
                      Medido na tela publicada: `text-xs` deu **10,5px** (a raiz do app e' 14px e o
                      `text-*` do Tailwind e' rem), ou seja o total saiu MENOR que o `ORC-` de 11px
                      ao lado — o valor em jogo perdendo a hierarquia pro codigo, que e' so' a
                      etiqueta dele. */}
                  <div className="flex-shrink-0 leading-tight" style={{ width: COL.compra }}>
                    {(c.vendas ?? []).map((v) => {
                      // ⚠️ So' quando VENDEU outra pessoa (3 casos em 55): ai o nome diz algo que a
                      // coluna de quem atendeu nao diz. Igual, ele seria a mesma palavra duas vezes.
                      const outroVendedor = v.vendedor && v.vendedor !== quemCuidou ? v.vendedor : null;
                      return (
                        <div key={v.numero} className="flex items-baseline justify-between gap-2">
                          {/* ⛔ **Na base o vendedor NAO vem colado no `ORC-`** — medido na tela
                              publicada em 10/09/2026: das 30 linhas com compra, 8 mostravam o
                              vendedor e **as 8 truncavam** (`ORC-005040 · GABRYE...`, 135px pedidos
                              em 100 disponiveis). No dia isso e' raro — 3 em 55 —, mas aqui a
                              ultima compra e' de qualquer epoca, entao quase sempre foi outra
                              pessoa. ✅ Ele desce pra segunda linha junto da data, onde ha os 176px
                              inteiros: nada trunca e nada se perde ([DA-705]). */}
                          <span className="text-[11px] tabular-nums truncate">
                            {v.numero}{!naBase && outroVendedor ? ` · ${outroVendedor}` : ''}
                          </span>
                          {/* ⛔ O TOTAL nao encolhe nem quebra ([DA-703]): quem cede espaco e' o
                              `ORC-` ao lado, que ja trunca. Sem isto o flex espremia o valor ate
                              parti-lo em duas linhas — e o numero em jogo e' justamente o que a
                              coluna existe pra mostrar ([DA-561]). */}
                          {/* ⚠️ **O total saiu do `--num-destaque` e entrou no preto do registro**
                              ([DA-707], mesma instrucao dele). A hierarquia nao se perdeu: ele
                              segue **13px e bold** contra os 11px normais do `ORC-` ao lado — o
                              que mudou foi a COR, que era o navy `#1e3a8a` do token de destaque. */}
                          <span className="text-[13px] font-bold tabular-nums flex-shrink-0 whitespace-nowrap">
                            R$ {fmtBRL(v.total)}
                          </span>
                        </div>
                      );
                    })}
                    {/* ⚠️ **Na base a DATA e' obrigatoria; no dia ela seria ruido.** La o recorte da
                        aba ja diz quando foi — todas as vendas sao do mesmo dia. Aqui a coluna
                        responde *"quando esta pessoa comprou pela ultima vez"*, e um valor sem data
                        nao responde nada: `R$ 67,00` pode ser de ontem ou de maio. Vai numa segunda
                        linha pra nao disputar espaco com o total, que e' o numero em jogo ([DA-561]). */}
                    {naBase && (c.vendas ?? []).map((v) => v.quando && (
                      <div key={`quando-${v.numero}`} className="text-[11px] tabular-nums text-right truncate">
                        {formatDataSP(v.quando)}
                        {v.vendedor && v.vendedor !== quemCuidou ? ` · ${v.vendedor}` : ''}
                      </div>
                    ))}
                  </div>

                  {/* ⚠️ **OS FLYS QUE FORAM PRO CONTATO** — pedido dele em 09/09/2026 (*"enviei um
                      fly para o Rodolfo Bontempo, tem como exibir esta informacao em uma coluna?
                      [...] com os itens e a data"*), aprovado no esboco antes de existir codigo.
                      ⛔ **Aqui o item vai ESCRITO, nao atras de um selo `✦ N` como na grade**
                      ([DA-683] p.5): la a coluna tem 48px e nome de produto nao cabe; aqui a coluna
                      do nome sobra — medido na tela publicada, 987px numa janela de 1920 —, entao
                      os 236px saem inteiros dela e **nenhuma coluna existente se move** (o nome cai
                      pra 741, zero texto truncado, zero estouro lateral).
                      ⚠️ Largura FIXA em PIXEL, como as vizinhas: e' o que mantem a coluna das
                      etiquetas alinhada em todas as linhas, inclusive nas 8 de 12 que hoje nao tem
                      fly nenhum.
                      ⚠️ **A caixa fica VAZIA quando nao houve fly** — nao ha "nenhum fly": o vazio
                      ja responde, e escrever seria repetir a mesma informacao ([DA-500]), do mesmo
                      jeito que a coluna de vendas ao lado se cala. */}
                  <div className="flex-shrink-0 leading-tight" style={{ width: COL.flys }}>
                    {(c.flys ?? []).map((f, i) => (
                      <div key={`fly-${f.quando}-${i}`}>
                        <div className="flex items-baseline gap-1.5">
                          {/* Selo `✦` no par FIXO da casa (`#fde68a` + `#000`): variavel de tema
                              nos dois lados nao garante contraste entre eles ([DA-678]). */}
                          <span
                            className="flex-shrink-0 rounded-full font-bold"
                            style={{ backgroundColor: '#fde68a', color: '#000', fontSize: 10.5, padding: '1px 6px' }}
                            title={`Fly enviado${f.quem ? ` por ${f.quem}` : ''} · ultimos ${dias} dias`}
                          >
                            ✦
                          </span>
                          {/* ⚠️ Fly sem produto nao e' erro de leitura: o disparo automatico ate
                              08/08/2026 gravava `metadata` vazia, e promocao vai pelo rotulo. */}
                          <span className="text-[11px] font-semibold truncate">
                            {f.produtos ?? (f.de_promocao ? 'Promocao' : 'Fly')}
                          </span>
                        </div>
                        <div className="text-[11px] tabular-nums" style={{ paddingLeft: 23 }}>
                          {formatDataSP(f.quando)} {formatHoraSP(f.quando)}
                        </div>
                      </div>
                    ))}
                  </div>

                  {/* ⚠️ **A etiqueta saiu de baixo do nome e virou COLUNA** (08/09/2026, print dele
                      com o retangulo vermelho exatamente neste vazio + *"Exiba as Etiquetas dos
                      Contatos"*). Colada no nome ela nunca se alinhava — nome curto e nome longo
                      jogam a pill pra posicoes diferentes em cada card, entao ler a lista de cima a
                      baixo exigia procurar. Em coluna de largura fixa as pills empilham na MESMA
                      abcissa e "quem tem / quem nao tem" se le num passe de olho, que e' o gesto
                      pra que esta aba existe. ⛔ `w-[260px]` em pixel, nunca `w-64`: a raiz do app
                      e' 14px e o `w-*` do Tailwind e' rem — `w-64` daria 224, nao 256. */}
                  {/* ⚠️ **Era 260px e caiu pra 180 em 09/09/2026** ([DA-703]): o `+` dos itens tomou
                      34px do bloco do nome, que e' `flex-1` e paga toda conta nova — 16 dos 23 nomes
                      passaram a truncar, e `Rodolfo Bontempo` virou `Rodolfo Bont...`.
                      ⛔ **A largura devolvida saiu daqui porque foi a unica coluna MEDIDA com folga
                      em TODAS as 23 linhas**: pior caso 110px de 260 (as outras — orcamento, flys,
                      hora — usam o que tem, e a dos flys ja falta 79px). Uma linha so' nao provaria:
                      a folga varia com quantas etiquetas o contato tem.
                      ⚠️ Cortar aqui nao corta TEXTO — os chips fazem `flex-wrap` e empilham. */}
                  {/* ⚠️ **A COLUNA E' O BOTAO desde 10/09/2026** ([DA-705]). Ele pediu pra tirar o
                      botao solido `Etiquetar` do fim da linha — mas ele era o UNICO caminho pra
                      marcar ali mesmo, que e' a decisao de 08/09 que fez esta aba existir ([DA-691]:
                      *"marcar ali mesmo, nao so' exibir"*). Tirar sem repor deixaria a aba sem o
                      gesto que ela serve. ✅ Entao o alvo passou a ser a propria coluna: clicar nas
                      pills (ou no `sem etiqueta`) abre o mesmo seletor. Some um botao de 160px e
                      nao some nenhuma acao.
                      ⛔ E' `<button>` de verdade, nao `<div onClick>`: sem isso perde foco por
                      teclado e o leitor de tela nao anuncia que ali ha uma acao.
                      ⚠️ `w-full` + `justify-start` mantem a caixa clicavel do tamanho da COLUNA, e
                      nao do texto — numa linha com uma etiqueta so' o alvo seria de 60px. */}
                  <button
                    type="button"
                    onClick={() => { setEditandoNome(null); setEditando(aberto ? null : c.id); }}
                    disabled={gravando === c.id}
                    title={aberto ? 'Fechar o seletor de etiquetas' : 'Clique para etiquetar este contato'}
                    aria-expanded={aberto}
                    className="flex items-center gap-1 flex-wrap flex-shrink-0 justify-start text-left rounded px-1 py-0.5 hover:opacity-70"
                    style={{ width: COL.etiquetas, background: 'transparent', border: 'none', cursor: 'pointer' }}
                  >
                    {visiveisDoContato.length > 0 ? (
                      visiveisDoContato.map((e) => (
                        <BadgeEtiqueta key={e.id} nome={e.nome} cor={e.cor} apelido={e.apelido} />
                      ))
                    ) : (
                      <span className="text-[11px] font-semibold">
                        sem etiqueta
                      </span>
                    )}
                  </button>

                  {/* ⚠️ **O balaozinho mora AQUI desde 10/09/2026** ([DA-705], pedido dele: *"tem
                      como colocar so' o balaozinho do Whatsapp ao lado da Etiqueta e retirar o botao
                      Etiquetar?"*). Largura FIXA e reservada mesmo quando o contato nao tem telefone
                      — elemento condicional no meio da linha desloca os vizinhos e desfaz o
                      alinhamento na linha em que ele falta. */}
                  {/* ⚠️ **O aviaozinho entrou AO LADO do balaozinho em 10/09/2026** ([DA-707],
                      pedido dele). Ele abre a MESMA tela de fly do chat (`ModalEnviarMaterial`) —
                      sem passar pelo Atendimento, que era o caminho de hoje: sair da lista, achar a
                      conversa, mandar, voltar.
                      ⛔ **A palavra `CHAT` da coluna ao lado NAO saiu**, embora o pedido tenha
                      nascido assim: ela nao e' rotulo do botao, e' a coluna ORIGEM da [DA-703] —
                      por onde a pessoa apareceu hoje. Confirmado com ele antes de construir
                      (*"sim, aviaozinho ao lado do balao, sem perder nada"*). As duas coisas
                      parecem a mesma por serem vizinhas: o balaozinho ABRE a conversa, a palavra
                      CONTA a procedencia.
                      ⚠️ Os dois botoes cabem nos mesmos 64px reservados, e a largura e' fixa
                      mesmo sem telefone — elemento condicional no meio da linha desloca os
                      vizinhos e desfaz o alinhamento na linha em que ele falta. */}
                  {/* ⚠️ **Numero invalido troca os dois botoes pela LIXEIRA** (pedido dele em
                      24/09/2026, a mesma forma da campanha): ali o WhatsApp nao entrega, entao
                      conversa e fly nao tem o que fazer, e lixeira ao lado deles convidaria o
                      clique errado nas linhas boas. */}
                  <div className="flex-shrink-0 flex items-center" style={{ width: COL.chat }}>
                    {c.numero_invalido ? (
                      <button
                        type="button"
                        onClick={() => void excluirContato(c)}
                        className="btn-icon"
                        style={{ color: '#dc2626' }}
                        title="Apagar este contato de vez (número que o WhatsApp não entrega)"
                        aria-label={`Apagar o contato ${nomeDe(c)}`}
                      >
                        <Trash2 size={17} />
                      </button>
                    ) : c.telefone && (
                      <>
                        <button
                          type="button"
                          onClick={() => onAbrirConversa(c.telefone!, nomeDe(c))}
                          className="btn-icon"
                          title="Abrir a conversa no Atendimento"
                        >
                          <MessageCircle size={18} />
                        </button>
                        <button
                          type="button"
                          onClick={() => abrirFly(c)}
                          disabled={abrindoFly === c.id}
                          className="btn-icon"
                          title="Enviar um fly para este contato, sem abrir o chat"
                        >
                          {abrindoFly === c.id
                            ? <Loader2 size={16} className="animate-spin" />
                            : <Send size={16} />}
                        </button>
                      </>
                    )}
                  </div>

                  {/* ⚠️ **ORIGEM** ([DA-703], pedido dele com o retangulo nesta faixa): por onde a
                      pessoa apareceu HOJE. `BALCAO` e' a AUSENCIA de chat — nao "foi capturado pela
                      trava" —, senao as 16 vendas de cliente ja cadastrado que compra presencial
                      ficariam sem origem. `CHAT` ganha quando fez as duas coisas (decisao dele).
                      ⛔ **Sem cor de fundo, de proposito.** Sao dois valores NEUTROS, e a [DA-678]
                      custou uma etiqueta a 2,7:1 por pintar fundo e fonte com variaveis de familias
                      diferentes; aqui o peso da fonte separa, e o [[user_daltonismo]] nao entra em
                      jogo. ⛔ Largura FIXA em pixel, como as vizinhas — a raiz do app e' 14px e o
                      `w-*` do Tailwind e' rem. */}
                  {/* ⚠️ Na base a celula pode ficar VAZIA: o contato importado nunca escreveu nem
                      comprou, e escrever `BALCAO` nele seria afirmar uma visita que ninguem viu. */}
                  <div className="flex-shrink-0 text-[11px] font-bold leading-tight" style={{ width: COL.origem }}>
                    {c.origem === 'BALCAO' ? 'BALCÃO' : c.origem === 'CHAT' ? 'CHAT' : ''}
                  </div>

                  {/* ÚLTIMA MENSAGEM ENVIADA — pedido dele em 10/09/2026. Mostra quando foi a
                      ultima vez que a loja enviou algo pro contato: hora se foi hoje, DD/MM se foi
                      outro dia, `Nunca` em italico se nunca. Util pra saber quem esta' sem contato.
                      ⚠️ **Mudou de lugar no mesmo dia**, a pedido dele: mora colada em `Recebidas`,
                      porque as duas respondem a mesma pergunta em direcoes opostas e a comparacao
                      so' e' barata quando elas sao vizinhas.
                      ⚠️ **O rotulo `ult. msg` que morava DENTRO da celula saiu** ([DA-707]): com o
                      cabecalho fixo por cima, ele era a mesma informacao dita duas vezes ([DA-500])
                      — e ainda gastava uma linha de altura em cada registro. */}
                  <div
                    className={`flex-shrink-0 leading-tight text-right text-[11px] tabular-nums font-semibold${c.ultima_msg ? '' : ' italic'}`}
                    style={{ width: COL.ultEnvio }}
                    title="Quando a loja enviou a ultima mensagem para este contato"
                  >
                    {c.ultima_msg ?? 'Nunca'}
                  </div>

                  {/* ⛔ **Largura FIXA aqui, senao a coluna das etiquetas nao alinha** — e' o que a
                      medicao na tela publicada pegou: `1 msg` e `11 msgs` tem larguras diferentes,
                      o bloco do nome e' `flex-1` e absorve a sobra, entao a etiqueta caia em tres
                      abcissas (895/892/885). Alinhar a coluna exige travar TUDO que vem depois
                      dela, nao so' ela. Mesmo motivo pro espaco do botao de conversa, que **sempre
                      existe** ainda que o contato nao tenha telefone: elemento condicional no meio
                      da linha desloca os vizinhos e desfaz o alinhamento na linha em que ele falta. */}
                  {/* ⚠️ Desde a [DA-703] a aba tambem lista quem **so comprou** e nunca escreveu
                      (o Rones, capturado no balcao). Nessas linhas a hora e' a da VENDA, nao a da
                      mensagem — entao embaixo vai a palavra `compra` no lugar de `0 msgs`. Numero
                      grande sem rotulo afirma outra coisa ([DA-561]), e `0 msgs` debaixo de um
                      horario leria como "escreveu e nao contamos". */}
                  {/* ⚠️ **Na base a celula troca de pergunta junto com a aba**: em cima, a DATA da
                      ultima mensagem (`08/09/25`) no lugar da hora; embaixo, o TOTAL de mensagens
                      no lugar das do dia. O contato que nunca escreveu nao tem carimbo nenhum —
                      `—` em cima e a celula de baixo calada, porque `0 msgs` debaixo de um traco
                      ja e' a mesma informacao dita duas vezes ([DA-500]).
                      ⛔ **A palavra `compra` exige VENDA, nao "zero mensagem"** (10/09/2026): desde
                      que a aba passou a listar quem a LOJA procurou, ha linha com zero mensagem e
                      zero venda — o contato novo que so' recebeu. Escrever `compra` nele seria
                      afirmar uma venda que ninguem fez, e a celula fica calada: quem conta o que
                      aconteceu com ele e' o `Ult. envio` ao lado. */}
                  <div className="text-right flex-shrink-0" style={{ width: COL.recebidas }}>
                    <div className="text-xs tabular-nums">{c.carimbo ?? (naBase ? '—' : '')}</div>
                    <div className="text-[11px] tabular-nums">
                      {c.mensagens > 0
                        ? contar(c.mensagens, 'msg', 'msgs')
                        : !naBase && c.vendas?.length ? 'compra' : ''}
                    </div>
                  </div>

                  {/* ⚠️ A ponta direita da linha ESVAZIOU em 10/09/2026 ([DA-705]): o lapis foi pro
                      lado do Sobrenome, o balaozinho foi pro lado das etiquetas e o botao solido
                      `Etiquetar` saiu — a coluna das etiquetas virou o proprio alvo do clique. O
                      espaco liberado (~192px) volta pro bloco do nome, que e' o unico `flex-1`. */}
                </div>

                {/* ⚠️ **O seletor e' uma GRADE de chips, nao um fluxo de caixas de marcacao**
                    ([DA-694], escolha dele no esboco de 09/09/2026 — *"a exibicao das Etiquetas
                    esta muito feia"*). Tres coisas faziam a bagunca, e as tres se mediram na tela
                    publicada: (a) cada etiqueta gastava TRES elementos (caixa + selo + nome), com
                    o nome repetindo o selo em 8 das 15; (b) o `flex-wrap` quebrava onde coubesse,
                    entao nenhuma coluna alinhava e achar uma etiqueta virava caca; (c) 9 das 15
                    tem a MESMA cor, entao o selo colorido parecia separar e nao separava.
                    ⛔ `minmax(215px, 1fr)` em PIXEL, nunca em rem — a raiz do app e' 14px. */}
                {/* ⚠️ **A edicao pousa no MESMO lugar do seletor de etiquetas** — embaixo da linha,
                    dentro do card, com os dois mutuamente exclusivos.
                    ⛔ **Enter grava e Esc cancela**: sem isso o unico caminho de saida seria o
                    botao, e campo preenchido que depende de um segundo gesto e' onde o trabalho se
                    perde calado ([DA-697]).
                    ⚠️ `no-upper` como no `PainelContato`: o uppercase global da casa e' pra
                    cadastro, e nome de pessoa em caixa alta nao e' o que o chat mostra. */}
                {editandoNome?.id === c.id && (
                  <div className="mt-2 border rounded-lg p-2 flex items-end gap-2 flex-wrap">
                    <div style={{ width: 220 }}>
                      <label className="text-[11px] block mb-0.5" style={{ color: 'var(--text-muted)' }}>Nome</label>
                      <input
                        autoFocus
                        value={editandoNome.nome}
                        onChange={(e) => setEditandoNome({ ...editandoNome, nome: e.target.value })}
                        onKeyDown={(e) => { if (e.key === 'Enter') void gravarNome(); if (e.key === 'Escape') setEditandoNome(null); }}
                        className="no-upper w-full px-2.5 py-1.5 text-sm border rounded-lg outline-none focus:ring-2 focus:ring-blue-400"
                        placeholder="Nome"
                      />
                    </div>
                    <div style={{ width: 220 }}>
                      <label className="text-[11px] block mb-0.5" style={{ color: 'var(--text-muted)' }}>Sobrenome</label>
                      <input
                        value={editandoNome.sobrenome}
                        onChange={(e) => setEditandoNome({ ...editandoNome, sobrenome: e.target.value })}
                        onKeyDown={(e) => { if (e.key === 'Enter') void gravarNome(); if (e.key === 'Escape') setEditandoNome(null); }}
                        className="no-upper w-full px-2.5 py-1.5 text-sm border rounded-lg outline-none focus:ring-2 focus:ring-blue-400"
                        placeholder="Sobrenome"
                      />
                    </div>
                    <div style={{ width: 220 }}>
                      <label className="text-[11px] block mb-0.5" style={{ color: 'var(--text-muted)' }}>Telefone (WhatsApp)</label>
                      <input
                        value={editandoNome.telefone}
                        inputMode="tel"
                        onChange={(e) => setEditandoNome({ ...editandoNome, telefone: e.target.value, erro: null })}
                        onKeyDown={(e) => { if (e.key === 'Enter') void gravarNome(); if (e.key === 'Escape') setEditandoNome(null); }}
                        className="w-full px-2.5 py-1.5 text-sm border rounded-lg outline-none focus:ring-2 focus:ring-blue-400 tabular-nums"
                        placeholder="Digite com DDD"
                      />
                    </div>
                    <button type="button" onClick={() => void gravarNome()} disabled={gravando === c.id} className="btn-primario text-sm">
                      {gravando === c.id ? 'Gravando...' : 'Salvar'}
                    </button>
                    <button type="button" onClick={() => setEditandoNome(null)} className="btn-secundario text-sm">
                      Cancelar
                    </button>
                    {/* O nome do WhatsApp fica a vista: e' de onde a loja copia quando o contato
                        nunca foi batizado, e ele NAO e' editavel (quem o escolhe e' o cliente). */}
                    {c.push_name && (
                      <span className="text-[11px] self-center" style={{ color: 'var(--text-muted)' }}>
                        no WhatsApp: {c.push_name}
                      </span>
                    )}
                    {editandoNome.erro && (
                      <div className="w-full text-sm font-semibold rounded px-2 py-1" style={{ backgroundColor: '#fde68a', color: '#000' }}>
                        {editandoNome.erro}
                      </div>
                    )}
                  </div>
                )}

                {aberto && (
                  <div className="mt-2 border rounded-lg p-2">
                    {/* ⚠️ Na BASE (aba Contatos) as `nao_cliente` saem do seletor — pedido dele,
                        24/09/2026 —, MENOS as que ele marcou "Na Gestao" no Gerenciar (desde
                        15/10/2026, [DA-814] p.7). No modo `dia` elas FICAM: e' por la que ele as aplica ([DA-693]).
                        A que o contato ja carrega segue a vista, senao nao haveria como desmarcar. */}
                    <SeletorEtiquetas
                      etiquetas={naBase
                        // Desde 15/10/2026 a etiqueta de "nao cliente" com "Na Gestao" marcada no
                        // Gerenciar tambem entra ([DA-814] p.7) — quem decide e' ele.
                        ? etiquetasVisiveis.filter((e) => !e.nao_cliente || e.exibir_gestao || c.etiquetas.some((x) => x.id === e.id))
                        : etiquetasVisiveis}
                      marcadas={c.etiquetas.map((x) => x.id)}
                      onAlternar={(id) => void alternar(c, id)}
                      desabilitado={gravando === c.id}
                      avisoEmDuasColunas
                    />
                    <div className="mt-2 flex justify-end">
                      <button
                        className="btn-primario text-sm px-3 py-1"
                        onClick={() => setEditando(null)}
                      >
                        Fechar
                      </button>
                    </div>
                  </div>
                )}

                {/* ⚠️ **OS ITENS DA COMPRA** ([DA-703]). Uma tabela por venda, porque o mesmo contato
                    pode ter comprado varias vezes no dia (o DEVAIR fez 4) e misturar tudo esconderia
                    o que foi junto com o que. O `ORC-` vai no cabecalho de cada uma.
                    ⛔ A gaveta convive com a das etiquetas em vez de disputar: as duas podem estar
                    abertas, porque o gesto e' JUSTAMENTE olhar os itens e etiquetar em seguida —
                    fechar uma pra abrir a outra tiraria da tela a informacao que embasa a decisao. */}
                {itensAbertos && (
                  <div className="mt-2 flex flex-col gap-2">
                    {vendas.filter((v) => v.venda_id != null).map((v) => {
                      const itens = itensPorVenda[v.venda_id!];
                      return (
                        <div key={v.numero} className="border rounded-lg overflow-hidden"
                             style={{ borderColor: 'var(--input-border, #e5e7eb)' }}>
                          <div className="flex items-baseline justify-between px-3 py-1.5 text-[11px] font-bold"
                               style={{ background: 'var(--grid-header-bg)', color: 'var(--grid-header-fg)' }}>
                            <span>{v.numero}{v.vendedor ? ` · ${v.vendedor}` : ''}</span>
                            <span className="tabular-nums">R$ {fmtBRL(v.total)}</span>
                          </div>
                          {itens === undefined || itens === null ? (
                            <div className="px-3 py-3 text-center text-[12px]" style={{ color: 'var(--text-muted)' }}>
                              Carregando os itens...
                            </div>
                          ) : itens.length === 0 ? (
                            <div className="px-3 py-3 text-center text-[12px]" style={{ color: 'var(--text-muted)' }}>
                              Esta venda nao tem itens.
                            </div>
                          ) : (
                            <div className="flex flex-col">
                              {itens.map((it) => (
                                <div key={it.id} className="flex items-center gap-3 px-3 py-1.5 border-t"
                                     style={{ borderColor: 'var(--input-border, #f3f4f6)' }}>
                                  {/* ⛔ `FotoProduto`, nunca um `<img>` na mao: a peca traz o
                                      `object-fit: contain` + fundo branco da [DA-526] — com `cover`
                                      um cabo ou uma barra viram uma faixa do meio, sem o rotulo que
                                      identifica o produto, que e' justamente o que se quer ler aqui. */}
                                  {it.foto_url ? (
                                    <FotoProduto url={it.foto_url} tam={36} />
                                  ) : (
                                    <div className="flex-shrink-0 rounded"
                                         style={{ width: 36, height: 36, background: 'var(--grid-header-bg)',
                                                  border: '1px solid var(--input-border, #e5e7eb)' }} />
                                  )}
                                  <span className="text-[11px] tabular-nums flex-shrink-0" style={{ width: 76 }}>
                                    {it.codigo_interno ?? '—'}
                                  </span>
                                  <span className="text-[12px] flex-1 min-w-0">{it.produto_descricao ?? '—'}</span>
                                  <span className="text-[12px] tabular-nums flex-shrink-0 text-right" style={{ width: 84 }}>
                                    {Number(it.quantidade).toLocaleString('pt-BR', { maximumFractionDigits: 3 })}
                                    {it.unidade_sigla ? ` ${it.unidade_sigla}` : ''}
                                  </span>
                                  <span className="text-[12px] tabular-nums flex-shrink-0 text-right font-semibold whitespace-nowrap" style={{ width: 84 }}>
                                    R$ {fmtBRL(it.subtotal)}
                                  </span>
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* ── PAGINACAO ([DA-705]) — so' na base, e so' quando ha mais de uma pagina.
          ⚠️ **Diz QUAL faixa esta na tela, nao so' o numero da pagina**: "51-100 de 508" responde
          onde a pessoa esta na lista; "pagina 2" obriga a fazer a conta de cabeca.
          ⛔ Os botoes DIZEM por que estao desligados pelo `title`, e o primeiro/ultimo se apagam
          nas pontas — botao apagado e mudo e' o beco que o `CLAUDE.md` § UI proibe. */}
      {naBase && total > tamanho && (
        <div className="flex items-center justify-center gap-3 mt-3 text-sm">
          <button
            type="button"
            className="btn-secundario"
            disabled={pagina <= 1 || carregando}
            title={pagina <= 1 ? 'Voce ja esta na primeira pagina' : 'Pagina anterior'}
            onClick={() => setPagina((p) => Math.max(1, p - 1))}
          >
            ← Anterior
          </button>
          <span className="tabular-nums" style={{ color: 'var(--text-muted)' }}>
            {(pagina - 1) * tamanho + 1}–{Math.min(pagina * tamanho, total)} de{' '}
            <strong style={{ color: 'var(--num-destaque)' }}>{total}</strong>
          </span>
          <button
            type="button"
            className="btn-secundario"
            disabled={pagina * tamanho >= total || carregando}
            title={pagina * tamanho >= total ? 'Voce ja esta na ultima pagina' : 'Proxima pagina'}
            onClick={() => setPagina((p) => p + 1)}
          >
            Proxima →
          </button>
        </div>
      )}

      {/* O lightbox do chat, reusado: portal pro `<body>`, Esc/clique fora fecham, e ele ja traz
          girar, zoom e baixar. A legenda leva o nome pra saber de quem e' a foto ampliada. */}
      {fotoAberta && (
        <LightboxImagem
          imagens={[{ url: fotoAberta.url, legenda: fotoAberta.nome }]}
          onFechar={() => setFotoAberta(null)}
        />
      )}
    </div>
  );
}
